import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { money } from "../../../../lib/api";
import { useUsbScale } from "../../../../hooks/useUsbScale";
import {
  PlaybooksSheet,
  RowShipMenu,
  RowTagDialog,
  RowSplitDialog,
  FulfillmentSettingsPanel,
  SlipTemplatesPanel,
  VendorSkusPanel,
  ConnectorsPanel,
  AutomationRulesPanel,
  CarrierCredentialDialog,
  RuleEditorDialog,
  TemplateEditorDialog,
  SkuEditorDialog,
  ConnectorEditorDialog,
  ReportsPanel,
  RecipientsPanel,
  OrderQueuesPanel,
} from "../fulfillment";
import DailyOrdersTab from "./DailyOrdersTab";
import { SectionHeading, Stat } from "../shared";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../../../../components/ui/dropdown-menu";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "../../../../components/ui/popover";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "../../../../components/ui/hover-card";
import { toast } from "sonner";
import {
  Package, Sparkles, Zap, FileText, BarChart3, Clock, AlertTriangle,
  ShieldCheck, RefreshCw, Calendar, TrendingUp, DollarSign, Users,
  FileSignature, ExternalLink, CalendarClock, Boxes,
  Plus, Scale as ScaleIcon, MoreHorizontal, ChevronRight, Wallet,
  Settings as SettingsIcon,
  Printer, ClipboardList, Sunset, Truck, MapPin, Upload, ShoppingCart, Inbox,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// FulfillmentCenterTab — Phase 1 bulk operations (labels, pack slips,
// pickup scheduling). Ships-with-Verqo lives here for batch flows; the
// Daily Orders tab retains the per-order button for urgent one-offs.
// ─────────────────────────────────────────────────────────────────────
export default function FulfillmentCenterTab({ orders, reload, movement, onRescan }) {
  // Queue chip filter: when a queue is active, its matching-order id set
  // is intersected with the ready-to-ship list. `null` = no filter (All).
  const [activeQueueOrderIds, setActiveQueueOrderIds] = useState(null);
  const [queueFilterBusy, setQueueFilterBusy] = useState(false);

  // Draft-batch state — must be declared BEFORE the readyOrders memo
  // that references it (TDZ / `const`-block-scope). The other draft-batch
  // helpers (loader, dialog state) live further down where the useCallback
  // is defined.
  const [activeDraftBatchId, setActiveDraftBatchId] = useState(null);
  const [draftBatches, setDraftBatches] = useState([]);

  const readyOrders = useMemo(() => {
    const base = (orders || []).filter(o => o.status === "forwarded" && !o.tracking);
    let out = base;
    if (activeQueueOrderIds) out = out.filter(o => activeQueueOrderIds.has(o.id));
    if (activeDraftBatchId) {
      const draft = draftBatches.find(d => d.id === activeDraftBatchId);
      const set = new Set(draft?.order_ids || []);
      out = out.filter(o => set.has(o.id));
    }
    return out;
  }, [orders, activeQueueOrderIds, activeDraftBatchId, draftBatches]);
  // `filteredOrdersForFeed` · ALL orders (any status) with queue + draft
  // chips applied. Powers the unified DailyOrdersTab feed rendered on the
  // Orders sub-tab — so queue/draft filters carry through the merged view,
  // not just the legacy "ready to ship" subset.
  const filteredOrdersForFeed = useMemo(() => {
    let out = orders || [];
    if (activeQueueOrderIds) out = out.filter(o => activeQueueOrderIds.has(o.id));
    if (activeDraftBatchId) {
      const draft = draftBatches.find(d => d.id === activeDraftBatchId);
      const set = new Set(draft?.order_ids || []);
      out = out.filter(o => set.has(o.id));
    }
    return out;
  }, [orders, activeQueueOrderIds, activeDraftBatchId, draftBatches]);
  const shippedVerqo = useMemo(
    () => (orders || []).filter(o => o.fulfillment_source === "verqo_shipping" && o.tracking),
    [orders],
  );

  const [selected, setSelected] = useState(() => new Set());
  const [weight, setWeight] = useState(1);
  const [busy, setBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupDate, setPickupDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10);
  });
  const [pickupCarrier, setPickupCarrier] = useState("usps");
  const [pickupWindow, setPickupWindow] = useState(["10:00", "17:00"]);
  const [pickups, setPickups] = useState([]);

  // ── Fulfillment settings (Phase 2 + 3 + Package A) ──
  const [settings, setSettings] = useState({
    auto_buy_on_forward: false,
    default_weight_lb: 1,
    default_length_in: 10,
    default_width_in: 8,
    default_height_in: 4,
    preferred_carrier: "auto",
    ship_from_addresses: [],
    prefer_own_carriers: false,
    auto_insure_min_order_value: 0,
    address_validation_mode: "off",
    shipping_profiles: [],
    label_filetype: "PDF",
    print_slip_with_label: false,
  });
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // ── Phase 3 · BYO carriers & automation rules ──
  const [automationOpen, setAutomationOpen] = useState(false);
  const [carrierAccounts, setCarrierAccounts] = useState([]);
  const [supportedCarriers, setSupportedCarriers] = useState([]);
  const [carrierDialog, setCarrierDialog] = useState(null); // {carrier, params}
  const [rules, setRules] = useState([]);
  const [rulesMeta, setRulesMeta] = useState({ allowed_fields: {}, allowed_ops: {}, allowed_action_types: ["route", "tag", "warehouse", "insurance", "playbook"] });
  const [ruleDraft, setRuleDraft] = useState(null); // in-edit rule
  // Option A · Playbooks — saved shipping plans applied to selected orders.
  const [playbooksOpen, setPlaybooksOpen] = useState(false);
  const [playbooks, setPlaybooks]         = useState([]);
  const [queuesCount, setQueuesCount]     = useState(0);
  const [playbookDraft, setPlaybookDraft] = useState(null);
  const [playbookBusy, setPlaybookBusy]   = useState(false);
  // Option A · row-level "Ship ▾" dropdown state.
  const [rowMenuOid, setRowMenuOid] = useState(null);
  const [rowActionBusy, setRowActionBusy] = useState(false);
  const [rowTagOid, setRowTagOid] = useState(null);
  const [rowTagText, setRowTagText] = useState("");
  const [rowSplitOid, setRowSplitOid] = useState(null);
  const [rowSplitParts, setRowSplitParts] = useState(2);
  const [reprintBusy, setReprintBusy] = useState(false);
  // Iter 199 · DailyOrdersTab actions exposed to the card header.
  // The child (DailyOrdersTab) mutates `dailyActionsRef.current` on every
  // render with { rescan, openCsvImport, exportCsv } — so the card-header
  // Rescan / Import / Export buttons can invoke them via ref lookup.
  const dailyActionsRef = useRef({});
  const [dailyRescanning, setDailyRescanning] = useState(false);
  // Phase 4 · manifests + address parser
  const [manifests, setManifests] = useState([]);
  const [manifestBusy, setManifestBusy] = useState(false);
  // End-of-Day dialog · groups manifest generation + past manifests + pickup shortcut
  const [eodOpen, setEodOpen] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [parserOpen, setParserOpen] = useState(false);
  const [parserText, setParserText] = useState("");
  const [parserBusy, setParserBusy] = useState(false);
  // Phase 4 · gap-fill — Reports/revenue widget
  const [reportsOpen, setReportsOpen] = useState(false);
  const [reports, setReports] = useState(null);
  const [reportsDays, setReportsDays] = useState(30);
  // Phase 5 · scan-to-print + pick list + Phase 6 · balance + manual label
  const [scanOpen, setScanOpen]         = useState(false);
  const [scanCode, setScanCode]         = useState("");
  const [scanBusy, setScanBusy]         = useState(false);
  const [scanResult, setScanResult]     = useState(null);
  const [pickListBusy, setPickListBusy] = useState(false);
  const [balance, setBalance]           = useState(null);
  const [postageDialogOpen, setPostageDialogOpen] = useState(false);
  const [postageTopupSource, setPostageTopupSource] = useState("wallet"); // 'wallet' | 'card'
  const [paymentMethods, setPaymentMethods] = useState([]);
  const loadPostageWallet = useCallback(() => {
    return api.get("/postage-wallet")
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
  }, []);
  const [ledgerBusy, setLedgerBusy]     = useState(false);
  const [manualLabelOpen, setManualLabelOpen] = useState(false);
  // Inline live-rate quotes inside the Manual Label dialog — turns it
  // into a proper quote → buy flow. Rate compare moved off the toolbar
  // and lives in 3 natural places: per-row action, this dialog, and
  // ⋯ More → Tools (ad-hoc fallback).
  const [manualQuotes, setManualQuotes] = useState([]);
  const [manualQuoteBusy, setManualQuoteBusy] = useState(false);
  const [manualForm, setManualForm] = useState({
    to_name:"", to_street1:"", to_city:"", to_state:"", to_zip:"", to_country:"US",
    weight_lb: 1, reference: "",
  });
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates]         = useState([]);
  const [templateEvents, setTemplateEvents] = useState({});

  // ── Phase 8 · Shipping ops depth ──
  const [historyOpen,       setHistoryOpen]     = useState(false);
  const [history,           setHistory]         = useState([]);
  const [historyBusy,       setHistoryBusy]     = useState(false);
  const [historyFilters,    setHistoryFilters]  = useState({ q: "", carrier: "", status: "", days: 30 });
  const [recipientsOpen,    setRecipientsOpen]  = useState(false);
  const [recipients,        setRecipients]      = useState([]);
  const [recipientQ,        setRecipientQ]      = useState("");
  const [recipientDraft,    setRecipientDraft]  = useState(null); // form data
  const [adjustmentsOpen,   setAdjustmentsOpen] = useState(false);
  const [adjustments,       setAdjustments]     = useState(null); // {adjustments, count, total_amount}
  const [rateCompareOpen,   setRateCompareOpen] = useState(false);
  const [rateCompareOid,    setRateCompareOid]  = useState("");
  const [rateCompareRates,  setRateCompareRates]= useState([]);
  const [rateCompareBusy,   setRateCompareBusy] = useState(false);
  const [qrReturnRow,       setQrReturnRow]     = useState(null); // shipment doc
  const [qrReturnBusy,      setQrReturnBusy]    = useState(false);
  const [slaOpen,           setSlaOpen]         = useState(false);
  const [slaShipBy,         setSlaShipBy]       = useState("");
  const [slaHoldUntil,      setSlaHoldUntil]    = useState("");
  const [slaBusy,           setSlaBusy]         = useState(false);
  const [bulkVoidBusy,      setBulkVoidBusy]    = useState(false);
  const [bulkVoidResult,    setBulkVoidResult]  = useState(null);
  const [voidSelected,      setVoidSelected]    = useState(() => new Set());

  // ── UI refactor · 3-tab shell (Orders / Shipments / Settings) — Feb 6, 2026 ──
  // Prior 4-tab shell (Orders / Queues / Batches / Setup) collapsed:
  //  · Queues → filter chip strip inside Orders + "Manage queues" dialog.
  //  · Batches → contextual action bar in Orders (when N selected) + batch history inside Shipments.
  //  · Shipment history → promoted from "⋯ More" to a first-class Shipments tab
  //    with a toggle: [Individual labels] [Grouped by batch].
  const [subTab, setSubTab] = useState(() => {
    try {
      const stored = localStorage.getItem("verqoDropshipSubTab") || "orders";
      // Migrate legacy tab keys that no longer exist.
      if (stored === "queues" || stored === "batches") return "orders";
      if (stored === "setup") return "setup";
      return stored;
    } catch { return "orders"; }
  });
  // Shipments sub-view toggle: 'labels' (individual shipments) | 'batches'
  // (grouped by batch). Feb 6 2026: briefly hosted an 'all_orders' view too,
  // but that content was relocated to the Orders sub-tab so sellers see the
  // daily-orders KPIs / filters / Import Tracking / Export / per-row actions
  // without an extra click. Any 'all_orders' localStorage value migrates back
  // to 'labels' on next render.
  const [shipmentsView, setShipmentsView] = useState(() => {
    try {
      const stored = localStorage.getItem("verqoDropshipShipmentsView");
      if (!stored) return "labels";
      if (stored === "all_orders") return "labels";
      return stored;
    }
    catch { return "labels"; }
  });
  useEffect(() => {
    try { localStorage.setItem("verqoDropshipShipmentsView", shipmentsView); } catch {/**/}
  }, [shipmentsView]);
  // Active queue chip (filters Orders). null = "All" (no filter).
  // Persistent across sub-tabs — set on Orders, filter still applies on Shipments.
  const [activeQueueId, setActiveQueueId] = useState(null);
  const [queues, setQueues] = useState([]);
  const [queuesDialogOpen, setQueuesDialogOpen] = useState(false);
  // Draft Batches — pre-purchase groupings on the Orders sub-tab.
  // NOTE: `activeDraftBatchId` and `draftBatches` are hoisted above the
  // readyOrders memo (line ~57) to avoid a TDZ crash.
  const [draftBatchDialogOpen, setDraftBatchDialogOpen] = useState(false);
  const [draftBatchEditing, setDraftBatchEditing] = useState(null);   // { id?, name, color }
  const loadDraftBatches = useCallback(() => {
    return api.get("/dropship/draft-batches")
      .then(r => setDraftBatches(r.data?.draft_batches || []))
      .catch(() => {/**/});
  }, []);
  useEffect(() => { loadDraftBatches(); }, [loadDraftBatches]);
  // Refresh queues list on tab change + on dialog close (so create/edit propagates).
  useEffect(() => {
    let alive = true;
    api.get("/dropship/order-queues")
      .then(r => {
        if (!alive) return;
        const qs = r.data?.queues || [];
        setQueues(qs);
        setQueuesCount(qs.length);
      })
      .catch(() => {/* silent */});
    return () => { alive = false; };
  }, [subTab, queuesDialogOpen]);

  // Iter 246 · Per-order queue membership lookup so each row can show
  // "in Queue X" chips. One call returns { orderId: [{id,name,color}...] }
  // for every queue this seller owns — the seller can spot at a glance
  // which rule is (or isn't) picking up a stuck order. Refreshes on the
  // same triggers as the queues list (create/edit/delete in the dialog).
  const [queueMembership, setQueueMembership] = useState({});
  useEffect(() => {
    let alive = true;
    api.get("/dropship/order-queues/membership")
      .then(r => { if (alive) setQueueMembership(r.data?.membership || {}); })
      .catch(() => {/* silent · row chips just skip */});
    return () => { alive = false; };
  }, [queuesDialogOpen, orders]);

  // Iter 246 · Reverse lookup { orderId → draft batch } built from the
  // draftBatches array we already fetch. O(N) once per draftBatches
  // change — cheap even at 1000s of orders.
  const draftBatchByOrder = useMemo(() => {
    const map = {};
    for (const b of draftBatches || []) {
      for (const oid of (b.order_ids || [])) {
        map[oid] = { id: b.id, name: b.name, color: b.color || "slate" };
      }
    }
    return map;
  }, [draftBatches]);

  // Iter 246 · Apply a queue filter — shared by the chip strip (top of the
  // Orders sub-tab) AND the new per-row queue chips inside the customer
  // cell. Reverse-derives matching order IDs from `queueMembership` (in
  // memory) so it doesn't need to fire another API call.
  const applyQueueFilter = useCallback((qid) => {
    if (!qid) {
      setActiveQueueId(null);
      setActiveQueueOrderIds(null);
      return;
    }
    const ids = new Set();
    for (const [oid, qs] of Object.entries(queueMembership || {})) {
      if ((qs || []).some(q => q.id === qid)) ids.add(oid);
    }
    setActiveQueueOrderIds(ids);
    setActiveQueueId(qid);
  }, [queueMembership]);

  // Iter 246 · Static class-name map for the per-row queue + batch chips.
  // Dynamic `bg-${color}-50` template classes DO NOT survive Tailwind JIT
  // (the compiler can't statically detect them), so we pre-compose the
  // full class strings here. Any new palette a seller can pick must land
  // in this map or the chip renders unstyled.
  const chipClassByColor = {
    indigo:  "border-indigo-200  bg-indigo-50  text-indigo-700  hover:bg-indigo-100",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    amber:   "border-amber-200   bg-amber-50   text-amber-700   hover:bg-amber-100",
    rose:    "border-rose-200    bg-rose-50    text-rose-700    hover:bg-rose-100",
    sky:     "border-sky-200     bg-sky-50     text-sky-700     hover:bg-sky-100",
    violet:  "border-violet-200  bg-violet-50  text-violet-700  hover:bg-violet-100",
    slate:   "border-slate-200   bg-slate-50   text-slate-700   hover:bg-slate-100",
    zinc:    "border-zinc-200    bg-zinc-50    text-zinc-700    hover:bg-zinc-100",
  };
  const chipClass = (color) => chipClassByColor[color] || chipClassByColor.indigo;
  // ── Setup hub · which config section is being shown under the Setup subtab ──
  const [setupSection, setSetupSection] = useState(() => {
    try { return localStorage.getItem("verqoDropshipSetupSection") || "profiles"; }
    catch { return "profiles"; }
  });
  useEffect(() => {
    try { localStorage.setItem("verqoDropshipSetupSection", setupSection); } catch {/**/}
  }, [setupSection]);
  useEffect(() => {
    try { localStorage.setItem("verqoDropshipSubTab", subTab); } catch {/**/}
    // Auto-open the panels most relevant for the chosen subtab so
    // sellers land on the right workflow without extra clicks.
    if (subTab === "shipments") {
      // Shipments tab drives its own panels via `shipmentsView`. Close
      // everything else so the view stays focused.
      setSettingsOpen(false); setTemplatesOpenB(false); setSkusOpen(false); setConnectorsOpen(false);
      setRecipientsOpen(false); setAdjustmentsOpen(false); setReportsOpen(false);
      setAutomationOpen(false);
    } else if (subTab === "setup") {
      // Setup hub takes over. Sync legacy xxxOpen flags to the current section.
      // Panels are rendered by their existing conditional blocks, so we drive
      // visibility here.
    } else {
      // Orders: close all config panels so the Orders view is clean.
      setSettingsOpen(false); setTemplatesOpenB(false); setSkusOpen(false); setConnectorsOpen(false);
      setBatchesOpen(false); setHistoryOpen(false); setRecipientsOpen(false); setAdjustmentsOpen(false);
      setReportsOpen(false); setAutomationOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  // Whenever we're on Settings subtab, keep only the selected section's panel open.
  useEffect(() => {
    if (subTab !== "setup") return;
    setSettingsOpen(setupSection === "profiles");
    setTemplatesOpenB(setupSection === "templates");
    setSkusOpen(setupSection === "skus");
    setConnectorsOpen(setupSection === "connectors");
    setRecipientsOpen(setupSection === "address");
    setAutomationOpen(setupSection === "automation");
    // Adjustments moved to ⋯ More (Iter 187) — keep panel closed by default in Settings.
    if (setupSection !== "adjustments") setAdjustmentsOpen(false);
    // Non-setup panels stay closed here.
    setBatchesOpen(false); setHistoryOpen(false); setReportsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, setupSection]);

  // ── Package A · Shipping profiles + batch history ──
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [batchesOpen,       setBatchesOpen]       = useState(false);
  const [batches,           setBatches]           = useState([]);
  const [batchesBusy,       setBatchesBusy]       = useState(false);
  const [batchesDays,       setBatchesDays]       = useState(30);

  // ── Package B · Pack-slip templates + Vendor SKU map ──
  const [templatesOpenB,    setTemplatesOpenB]    = useState(false);
  const [templatesList,     setTemplatesList]     = useState([]);
  const [templateDraft,     setTemplateDraft]     = useState(null); // form data or null
  const [skusOpen,          setSkusOpen]          = useState(false);
  const [skusList,          setSkusList]          = useState([]);
  const [skusQuery,         setSkusQuery]         = useState("");
  const [skuDraft,          setSkuDraft]          = useState(null);   // {id?, buyer_sku, vendor_sku, product_name?}
  const [skusCsvBusy,       setSkusCsvBusy]       = useState(false);

  // ── Package C · Connectors (ERP / ShipStation / custom webhook & API) ──
  const [connectorsOpen,    setConnectorsOpen]    = useState(false);
  const [connectors,        setConnectors]        = useState([]);
  const [connectorMeta,     setConnectorMeta]     = useState({ kinds: [], events: [] });
  const [connectorDraft,    setConnectorDraft]    = useState(null);
  const [connectorEvents,   setConnectorEvents]   = useState({ cid: null, list: [] });
  const [connectorBusy,     setConnectorBusy]     = useState(false);

  // ── Package D · USB shipping scale (WebHID) ──
  const scale = useUsbScale();
  const [scaleAutoFill, setScaleAutoFill] = useState(true);
  // Auto-fill the manual/bulk weight input whenever a stable reading arrives.
  useEffect(() => {
    if (scaleAutoFill && scale.stable && scale.weightLb && scale.weightLb > 0.05) {
      setWeight(String(scale.weightLb));
    }
  }, [scale.stable, scale.weightLb, scaleAutoFill]);
  useEffect(() => {
    if (!reportsOpen) return;
    api.get(`/dropship/fulfillment/reports?days=${reportsDays}`)
      .then(r => setReports(r.data))
      .catch(() => setReports(null));
  }, [reportsOpen, reportsDays]);
  useEffect(() => {
    if (!postageDialogOpen) return;
    api.get("/payment-methods").then(r => setPaymentMethods(r.data?.methods || r.data || [])).catch(() => setPaymentMethods([]));
    loadPostageWallet();
  }, [postageDialogOpen, loadPostageWallet]);

  useEffect(() => {
    // Postage wallet balance — cheap fetch on mount.
    loadPostageWallet();
    api.get("/dropship/notification-templates")
      .then(r => {
        setTemplates(r.data?.templates || []);
        setTemplateEvents(r.data?.events || {});
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/dropship/fulfillment/pickups").then(r => setPickups(r.data || [])).catch(() => {});
    api.get("/dropship/fulfillment/settings")
      .then(r => setSettings({
        auto_buy_on_forward: !!r.data?.auto_buy_on_forward,
        default_weight_lb: Number(r.data?.default_weight_lb) || 1,
        default_length_in: Number(r.data?.default_length_in) || 10,
        default_width_in:  Number(r.data?.default_width_in)  || 8,
        default_height_in: Number(r.data?.default_height_in) || 4,
        preferred_carrier: r.data?.preferred_carrier || "auto",
        ship_from_addresses: r.data?.ship_from_addresses || [],
        prefer_own_carriers: !!r.data?.prefer_own_carriers,
        auto_insure_min_order_value: Number(r.data?.auto_insure_min_order_value) || 0,
        address_validation_mode: r.data?.address_validation_mode || "off",
        shipping_profiles: r.data?.shipping_profiles || [],
        label_filetype: r.data?.label_filetype || "PDF",
        print_slip_with_label: !!r.data?.print_slip_with_label,
      }))
      .catch(() => { /* keep defaults */ });
    api.get("/dropship/carrier-accounts")
      .then(r => {
        setCarrierAccounts(r.data?.accounts || []);
        setSupportedCarriers(r.data?.supported_carriers || []);
      })
      .catch(() => {});
    api.get("/dropship/automation-rules")
      .then(r => {
        setRules(r.data?.rules || []);
        setRulesMeta({
          allowed_fields: r.data?.allowed_fields || {},
          allowed_ops:    r.data?.allowed_ops    || {},
          allowed_action_types: r.data?.allowed_action_types || ["route", "tag", "warehouse", "insurance", "playbook"],
        });
      })
      .catch(() => {});
    api.get("/dropship/playbooks")
      .then(r => setPlaybooks(r.data?.playbooks || []))
      .catch(() => {});
    api.get("/dropship/fulfillment/manifests")
      .then(r => setManifests(r.data?.manifests || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (settings.default_weight_lb) setWeight(settings.default_weight_lb);
  }, [settings.default_weight_lb]);

  const patchSettings = (fn) => { setSettings(s => fn({ ...s })); setSettingsDirty(true); };
  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      const { data } = await api.put("/dropship/fulfillment/settings", settings);
      setSettings({
        auto_buy_on_forward: !!data.auto_buy_on_forward,
        default_weight_lb: Number(data.default_weight_lb) || 1,
        default_length_in: Number(data.default_length_in) || 10,
        default_width_in:  Number(data.default_width_in)  || 8,
        default_height_in: Number(data.default_height_in) || 4,
        preferred_carrier: data.preferred_carrier || "auto",
        ship_from_addresses: data.ship_from_addresses || [],
        prefer_own_carriers: !!data.prefer_own_carriers,
        auto_insure_min_order_value: Number(data.auto_insure_min_order_value) || 0,
        address_validation_mode: data.address_validation_mode || "off",
        shipping_profiles: data.shipping_profiles || [],
        label_filetype: data.label_filetype || "PDF",
        print_slip_with_label: !!data.print_slip_with_label,
      });
      setSettingsDirty(false);
      toast.success("Fulfillment settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally { setSettingsBusy(false); }
  };

  const addWarehouse = () => patchSettings(s => {
    const isFirst = (s.ship_from_addresses || []).length === 0;
    s.ship_from_addresses = [...(s.ship_from_addresses || []), {
      name: `Warehouse ${(s.ship_from_addresses || []).length + 1}`,
      street1: "", city: "", state: "", zip: "", country: "US",
      is_default: isFirst,
    }];
    return s;
  });
  const removeWarehouse = (idx) => patchSettings(s => {
    s.ship_from_addresses = s.ship_from_addresses.filter((_, i) => i !== idx);
    if (s.ship_from_addresses.length && !s.ship_from_addresses.some(a => a.is_default)) {
      s.ship_from_addresses[0].is_default = true;
    }
    return s;
  });
  const setDefaultWarehouse = (idx) => patchSettings(s => {
    s.ship_from_addresses = s.ship_from_addresses.map((a, i) => ({ ...a, is_default: i === idx }));
    return s;
  });
  const updateWarehouse = (idx, field, value) => patchSettings(s => {
    s.ship_from_addresses = s.ship_from_addresses.map((a, i) => i === idx ? { ...a, [field]: value } : a);
    return s;
  });

  const returnLabel = async (o) => {
    if (o.return_label_url) {
      window.open(o.return_label_url, "_blank");
      return;
    }
    try {
      const { data } = await api.post(`/dropship/orders/${o.id}/return-label`, { weight_lb: settings.default_weight_lb || 1 });
      if (data.ok) {
        toast.success(`Return label generated · ${data.return_carrier} · ${data.return_tracking}`);
        if (data.return_label_url) window.open(data.return_label_url, "_blank");
        reload();
      } else {
        toast.error("Carrier declined the return label");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Return label failed");
    }
  };

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => setSelected(s => {
    if (s.size === readyOrders.length) return new Set();
    return new Set(readyOrders.map(o => o.id));
  });

  const bulkShip = async () => {
    if (selected.size === 0) return toast.error("Select at least one order");
    setBusy(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/dropship/fulfillment/bulk-ship", {
        order_ids: Array.from(selected),
        weight_lb: Number(weight) || 1,
        profile_id: selectedProfileId || null,
        batch_name: batchName?.trim() || null,
      });
      setBulkResult(data);
      if (data.purchased > 0) {
        toast.success(`${data.purchased} label${data.purchased === 1 ? "" : "s"} bought · ${data.failed} failed`);
        setSelected(new Set());
        reload();
        // Package A · auto-print combined label+slip PDF when the seller
        // opted in via Settings.
        if (data.auto_slip_with_label) {
          const okIds = (data.results || []).filter(r => r.ok).map(r => r.order_id);
          if (okIds.length) {
            try {
              const { data: pdf } = await api.post("/dropship/fulfillment/labels-with-slips", { order_ids: okIds });
              if (pdf?.html) {
                const win = window.open("", "_blank");
                if (win) { win.document.write(pdf.html); win.document.close(); }
              }
            } catch { /* silent — settings already told the user */ }
          }
        }
        // Package A · refresh the batches list if the panel is open.
        if (batchesOpen) loadBatches();
      } else {
        toast.error(`All ${data.failed} labels failed to purchase`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk-ship failed");
    } finally { setBusy(false); }
  };

  // ── Package A · Batches history loader ──
  const loadBatches = async () => {
    setBatchesBusy(true);
    try {
      const { data } = await api.get(`/dropship/fulfillment/batches?days=${batchesDays}`);
      setBatches(data.batches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load batches");
    } finally { setBatchesBusy(false); }
  };
  useEffect(() => {
    // Load batches whenever the panel that shows them is on-screen:
    //  1) legacy `batchesOpen` toggle (kept for internal callers)
    //  2) Shipments sub-tab in "batches" view (new IA)
    if (batchesOpen || (subTab === "shipments" && shipmentsView === "batches")) {
      loadBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchesOpen, batchesDays, subTab, shipmentsView]);

  // ── Package E1 · Preflight (validate + rate quotes) ──
  const [preflightBusy,    setPreflightBusy]    = useState(false);
  const [preflightResults, setPreflightResults] = useState(null);
  const runPreflight = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPreflightBusy(true); setPreflightResults(null);
    try {
      const { data } = await api.post("/dropship/preflight", {
        order_ids: Array.from(selected),
        weight_lb: Number(weight) || 1,
        profile_id: selectedProfileId || null,
      });
      setPreflightResults(data);
      const s = data.summary || {};
      toast.success(`Preflight: ${s.ok || 0} ready · ${s.address_error || 0} address issues · ${s.no_rates || 0} no rates`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preflight failed");
    } finally { setPreflightBusy(false); }
  };
  const clearRateCache = async () => {
    try {
      const { data } = await api.delete("/dropship/rate-cache?all=true");
      toast.success(`Cleared ${data.deleted} cached rates`);
    } catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); }
  };

  // ── Package E4 · AI Optimize ──
  const [optimizeBusy,    setOptimizeBusy]    = useState(false);
  const [optimizeResults, setOptimizeResults] = useState(null);
  const [useAiOpt,        setUseAiOpt]        = useState(false);
  const runOptimize = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setOptimizeBusy(true); setOptimizeResults(null);
    try {
      const { data } = await api.post("/dropship/optimize-rates", {
        order_ids: Array.from(selected),
        weight_lb: Number(weight) || 1,
        profile_id: selectedProfileId || null,
        use_ai:    useAiOpt,
      });
      setOptimizeResults(data);
      const okCount = (data.results || []).filter(r => r.recommended).length;
      toast.success(`Optimized ${okCount} order${okCount === 1 ? "" : "s"}${data.ai_used ? " · AI on" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Optimize failed");
    } finally { setOptimizeBusy(false); }
  };

  // ── Print helpers (Print labels · Print slips · Print combined) ──
  // Opens the returned HTML in a new tab; user's OS print dialog is
  // triggered by the embedded button. Warehouse workflow: label sticks
  // to the box, slip goes inside. `labels-with-slips` alternates the two
  // per order — the industry-standard "one-print pack station" flow.
  const openPrintWindow = (html, title) => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.document.title = title;
  };
  const printLabels = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPrintBusy(true);
    try {
      // Filter to selected orders that actually have a label_url.
      const eligible = readyOrders
        .filter(o => selected.has(o.id) && o.label_url)
        .concat(
          (orders || []).filter(o => selected.has(o.id) && o.label_url && o.status !== "forwarded"),
        );
      if (eligible.length === 0) {
        return toast.error("None of the selected orders have a bought label yet. Buy labels first.");
      }
      // Build a lightweight print sheet client-side: stack each label PDF/img.
      const pages = eligible.map(o => {
        const url = o.label_url;
        const isPdf = (url || "").toLowerCase().includes(".pdf");
        return isPdf
          ? `<div class="page"><iframe src="${url}" style="width:4in;height:6in;border:0"></iframe></div>`
          : `<div class="page"><img src="${url}" style="max-width:4in;max-height:6in;display:block"/></div>`;
      }).join("");
      const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Labels · ${eligible.length}</title>
<style>@page{size:4in 6in;margin:0}body{margin:0}.page{page-break-after:always;padding:0}.page:last-child{page-break-after:auto}
.print-btn{margin:10px;padding:6px 12px;font:12px system-ui}@media print{.print-btn{display:none}}</style></head>
<body><button class="print-btn" onclick="window.print()">Print ${eligible.length} labels</button>${pages}</body></html>`;
      openPrintWindow(html, `Labels · ${eligible.length}`);
      toast.success(`${eligible.length} label${eligible.length === 1 ? "" : "s"} ready to print`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Print labels failed");
    } finally { setPrintBusy(false); }
  };
  const printSlips = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPrintBusy(true);
    try {
      const { data } = await api.post("/dropship/fulfillment/pack-slips-batch",
        { order_ids: Array.from(selected) });
      openPrintWindow(data.html, `Pack slips · ${data.count}`);
      toast.success(`${data.count} pack slip${data.count === 1 ? "" : "s"} ready to print`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Print slips failed");
    } finally { setPrintBusy(false); }
  };
  const printCombined = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPrintBusy(true);
    try {
      const { data } = await api.post("/dropship/fulfillment/labels-with-slips",
        { order_ids: Array.from(selected) });
      openPrintWindow(data.html, `Labels + Slips · ${data.count}`);
      toast.success(`${data.count} label+slip pair${data.count === 1 ? "" : "s"} ready to print`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Combined print failed");
    } finally { setPrintBusy(false); }
  };

  // ── Pick-list print HTML builder (shared between toolbar button and
  // draft-batch quick-action). Keeps the CSS + row template in one
  // place so the two print flows stay in sync.
  const buildPickListHtml = (data, subtitle) => {
    const lines = data.lines || [];
    const rows = lines.map((l, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="qty">${l.quantity}</td>
        <td class="sku">${l.sku}</td>
        <td class="title">${l.title || ""}</td>
        <td class="bin">${l.bin_location || "—"}</td>
        <td class="orders">${(l.order_codes || []).join(", ")}</td>
        <td class="check"><span class="box"></span></td>
      </tr>`).join("");
    const subtitleHtml = subtitle
      ? `<span class="pill">${subtitle}</span>` : "";
    return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Pick list${subtitle ? " · " + subtitle : ""}</title>
<style>
  @page { size: letter; margin: 0.4in; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111; margin: 0; padding: 0.4in; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .meta { color: #666; font-size: 11px; margin-bottom: 14px; display: flex; gap: 18px; }
  .meta b { color: #111; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e5e5; vertical-align: top; }
  th { background: #f5f5f5; font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #555; }
  td.num { color: #999; width: 26px; }
  td.qty { font-weight: 700; font-size: 14px; width: 42px; text-align: right; }
  td.sku { font-family: ui-monospace, "SF Mono", monospace; font-size: 11px; color: #555; }
  td.title { font-weight: 500; }
  td.bin { font-family: ui-monospace, monospace; font-size: 11px; background: #fef3c7; padding: 4px 6px; border-radius: 3px; width: 70px; text-align: center; }
  td.orders { font-size: 10px; color: #777; max-width: 220px; }
  td.check { width: 34px; }
  .box { display: inline-block; width: 18px; height: 18px; border: 1.5px solid #333; border-radius: 3px; }
  .print-btn { background: #111; color: #fff; border: 0; padding: 8px 18px; font-size: 12px; border-radius: 20px; cursor: pointer; margin-bottom: 16px; font-family: inherit; }
  .pill { display: inline-block; background: #eef2ff; color: #4338ca; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; margin-left: 8px; vertical-align: middle; }
  @media print { .print-btn { display: none } }
  tfoot td { border-top: 2px solid #111; font-weight: 700; padding-top: 8px; }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print pick list</button>
  <h1>Pick list${subtitleHtml}</h1>
  <div class="meta">
    <div>Generated · <b>${new Date(data.generated_at).toLocaleString()}</b></div>
    <div>Orders · <b>${data.orders}</b></div>
    <div>Distinct SKUs · <b>${lines.length}</b></div>
    <div>Total units · <b>${data.total_units}</b></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th style="text-align:right">Qty</th><th>SKU</th><th>Product</th><th>Bin</th><th>Orders</th><th></th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td></td>
        <td class="qty">${data.total_units}</td>
        <td colspan="4">Total units to pick</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</body></html>`;
  };

  // ── Print pick list ─ aggregated shopping list across selected orders.
  // Warehouse walker uses this to gather inventory ONCE per SKU rather
  // than opening every order individually. Sorted by bin_location when
  // available (falls back to SKU sort).
  const printPickList = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPrintBusy(true);
    try {
      const { data } = await api.post("/dropship/fulfillment/pick-list",
        { order_ids: Array.from(selected) });
      const lines = data.lines || [];
      if (lines.length === 0) {
        return toast.error("Selected orders have no SKUs to pick");
      }
      openPrintWindow(buildPickListHtml(data), `Pick list · ${data.orders} orders`);
      toast.success(`Pick list ready · ${lines.length} SKUs · ${data.total_units} units`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pick list failed");
    } finally { setPrintBusy(false); }
  };

  // ── Package E4 · 3PL connector templates ──
  const [tplList, setTplList] = useState([]);
  useEffect(() => {
    api.get("/dropship/connector-templates").then(r => setTplList(r.data.templates || [])).catch(() => {});
  }, []);
  const install3PL = async (tid) => {
    const key = window.prompt(`Paste your API key for ${tid}`);
    if (!key || key.length < 3) return;
    try {
      await api.post("/dropship/connector-templates/install", { template_id: tid, api_key: key });
      toast.success(`${tid} connector installed`);
      loadConnectors();
    } catch (e) { toast.error(e.response?.data?.detail || "Install failed"); }
  };

  // ── Package E5 · Batch categorizer ──
  const [categorizeBy,      setCategorizeBy]      = useState("zone");
  const [categorizeResult,  setCategorizeResult]  = useState(null);
  const [categorizeBusy,    setCategorizeBusy]    = useState(false);
  const [batchName,         setBatchName]         = useState("");
  const runCategorize = async () => {
    if (selected.size === 0) return toast.error("Select orders first");
    setCategorizeBusy(true); setCategorizeResult(null);
    try {
      const { data } = await api.post("/dropship/orders/categorize", {
        order_ids: Array.from(selected),
        group_by: categorizeBy,
      });
      setCategorizeResult(data);
      toast.success(`Grouped ${data.total} orders into ${data.groups?.length || 0} batches by ${data.group_by}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Categorize failed"); }
    finally { setCategorizeBusy(false); }
  };
  const shipCategorizedGroup = async (group) => {
    setSelected(new Set(group.order_ids));
    // Give React a beat, then bulk-ship.
    setTimeout(() => bulkShip(), 100);
  };
  const renameBatch = async (bid, current) => {
    const next = window.prompt("New name for this batch:", current || "");
    if (!next || !next.trim()) return;
    try {
      await api.patch(`/dropship/fulfillment/batches/${bid}/name`, { name: next.trim() });
      toast.success("Batch renamed");
      loadBatches();
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };

  // ── Package B · Template + SKU handlers ──
  const loadTemplatesB = async () => {
    try {
      const { data } = await api.get("/dropship/pack-slip-templates");
      setTemplatesList(data.templates || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load templates"); }
  };
  useEffect(() => {
    if (templatesOpenB) loadTemplatesB();
  }, [templatesOpenB]);

  const openTemplateEditor = (tpl) => setTemplateDraft(tpl ? { ...tpl } : {
    name: "New template",
    logo_url: "",
    accent_color: "#111827",
    header_html: "",
    footer_html: "Thanks for your order",
    terms_html: "",
    show_prices: false,
    show_barcodes: true,
    show_vendor_sku: false,
    show_qty_check: true,
    warehouse_id: null,
    is_default: templatesList.length === 0,
  });

  const saveTemplateB = async () => {
    if (!templateDraft?.name?.trim()) return toast.error("Name required");
    try {
      if (templateDraft.id) {
        await api.patch(`/dropship/pack-slip-templates/${templateDraft.id}`, templateDraft);
      } else {
        await api.post("/dropship/pack-slip-templates", templateDraft);
      }
      toast.success("Template saved");
      setTemplateDraft(null);
      loadTemplatesB();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteTemplateB = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/dropship/pack-slip-templates/${tid}`);
      toast.success("Template removed");
      loadTemplatesB();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const previewTemplateB = async (tid) => {
    // Fetch a live pack slip for the first ready order using this template.
    const oid = readyOrders[0]?.id || (orders?.[0]?.id);
    if (!oid) return toast.error("Need at least one order to preview");
    try {
      const { data } = await api.get(`/dropship/fulfillment/pack-slip/${oid}`);
      if (data?.html) {
        const win = window.open("", "_blank");
        if (win) { win.document.write(data.html); win.document.close(); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const loadSkusB = async (q = "") => {
    try {
      const { data } = await api.get(`/dropship/vendor-skus${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setSkusList(data.skus || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load SKU map"); }
  };
  useEffect(() => {
    if (skusOpen) loadSkusB(skusQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skusOpen]);

  const saveSkuB = async () => {
    if (!skuDraft?.buyer_sku?.trim() || !skuDraft?.vendor_sku?.trim()) {
      return toast.error("Both SKUs required");
    }
    try {
      if (skuDraft.id) {
        await api.patch(`/dropship/vendor-skus/${skuDraft.id}`, skuDraft);
      } else {
        await api.post("/dropship/vendor-skus", skuDraft);
      }
      toast.success("Mapping saved");
      setSkuDraft(null);
      loadSkusB(skusQuery);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteSkuB = async (sid) => {
    try {
      await api.delete(`/dropship/vendor-skus/${sid}`);
      toast.success("Mapping removed");
      loadSkusB(skusQuery);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const importSkusCsvB = async (file) => {
    if (!file) return;
    setSkusCsvBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/dropship/vendor-skus/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.upserted} row${data.upserted === 1 ? "" : "s"} imported · ${data.errors?.length || 0} errors`);
      loadSkusB(skusQuery);
    } catch (e) { toast.error(e.response?.data?.detail || "CSV import failed"); }
    finally { setSkusCsvBusy(false); }
  };

  // ── Package C · Connector handlers ──
  const loadConnectors = async () => {
    try {
      const { data } = await api.get("/dropship/connectors");
      setConnectors(data.connectors || []);
      setConnectorMeta({
        kinds:  data.supported_kinds || [],
        events: data.supported_events || [],
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load connectors"); }
  };
  useEffect(() => { if (connectorsOpen) loadConnectors(); }, [connectorsOpen]);

  const emptyConnectorDraft = (kind = "custom_webhook") => ({
    id: null, kind, name: "",
    direction: kind === "shipstation" ? "import" : "push",
    is_active: true,
    events: ["order.shipped", "tracking.updated"],
    credentials: {},
    config: {},
  });

  const saveConnector = async () => {
    if (!connectorDraft?.name?.trim()) return toast.error("Name required");
    setConnectorBusy(true);
    try {
      if (connectorDraft.id) {
        await api.patch(`/dropship/connectors/${connectorDraft.id}`, connectorDraft);
      } else {
        await api.post("/dropship/connectors", connectorDraft);
      }
      toast.success("Connector saved");
      setConnectorDraft(null);
      loadConnectors();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setConnectorBusy(false); }
  };
  const deleteConnector = async (cid) => {
    if (!window.confirm("Delete this connector? All event history will be removed.")) return;
    try {
      await api.delete(`/dropship/connectors/${cid}`);
      toast.success("Connector removed");
      loadConnectors();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const testConnector = async (cid) => {
    try {
      const { data } = await api.post(`/dropship/connectors/${cid}/test`);
      const r = data.result || {};
      if (data.ok) toast.success(`Test OK · ${r.detail || ""}`);
      else toast.error(`Test failed · ${r.error || r.detail || "unknown"}`);
      loadConnectors();
    } catch (e) { toast.error(e.response?.data?.detail || "Test call failed"); }
  };
  const syncConnector = async (cid) => {
    try {
      const { data } = await api.post(`/dropship/connectors/${cid}/sync`);
      const r = data.result || {};
      if (r.error) toast.error(`Sync error · ${r.error}`);
      else toast.success(`Imported ${r.imported || 0} rows`);
      loadConnectors();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };
  const emitTestEvent = async (cid, event = "order.created") => {
    try {
      await api.post(`/dropship/connectors/${cid}/emit`, { event, payload: { demo: true } });
      toast.success(`Emitted ${event}`);
      setTimeout(() => showConnectorEvents(cid), 500);
    } catch (e) { toast.error(e.response?.data?.detail || "Emit failed"); }
  };
  const showConnectorEvents = async (cid) => {
    try {
      const { data } = await api.get(`/dropship/connectors/${cid}/events?limit=20`);
      setConnectorEvents({ cid, list: data.events || [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fetch events"); }
  };

  const printPackSlips = async () => {
    const ids = bulkResult?.results?.filter(r => r.ok).map(r => r.order_id) ||
                Array.from(selected);
    if (!ids.length) return toast.error("Select orders (or buy labels) first");
    try {
      const { data } = await api.post("/dropship/fulfillment/pack-slips-batch", { order_ids: ids });
      const win = window.open("", "_blank");
      if (!win) return toast.error("Pop-up blocked — allow pop-ups and retry");
      win.document.write(data.html);
      win.document.close();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build pack slips"); }
  };

  const openLabels = () => {
    (bulkResult?.results || []).filter(r => r.ok && r.label_url)
      .forEach(r => window.open(r.label_url, "_blank"));
  };

  const submitPickup = async () => {
    setPickupBusy(true);
    try {
      const { data } = await api.post("/dropship/fulfillment/schedule-pickup", {
        carrier:      pickupCarrier,
        pickup_date:  pickupDate,
        start_time:   pickupWindow[0],
        end_time:     pickupWindow[1],
        include_all_ready: true,
      });
      if (data.ok) {
        toast.success(`Pickup scheduled · ${data.transaction_count} label${data.transaction_count === 1 ? "" : "s"} · confirmation ${data.confirmation || "pending"}`);
        setPickupOpen(false);
        api.get("/dropship/fulfillment/pickups").then(r => setPickups(r.data || []));
      } else {
        toast.error(data.detail?.detail || data.detail?.message || "Pickup request declined by carrier");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pickup scheduling failed");
    } finally { setPickupBusy(false); }
  };

  // ── Phase 8 · handlers ──
  const loadHistory = async () => {
    setHistoryBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("days", String(historyFilters.days || 30));
      if (historyFilters.q) params.set("q", historyFilters.q);
      if (historyFilters.carrier) params.set("carrier", historyFilters.carrier);
      if (historyFilters.status) params.set("status", historyFilters.status);
      const { data } = await api.get(`/dropship/shipment-history?${params.toString()}`);
      setHistory(data.shipments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load shipment history");
    } finally { setHistoryBusy(false); }
  };
  useEffect(() => {
    // Load shipment history whenever the panel is on-screen:
    //  1) legacy `historyOpen` toggle (still used by ⋯ More if user opens it)
    //  2) Shipments sub-tab in "labels" view (new IA)
    if (historyOpen || (subTab === "shipments" && shipmentsView === "labels")) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, subTab, shipmentsView]);

  const loadRecipients = async (q = "") => {
    try {
      const { data } = await api.get(`/dropship/recipients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRecipients(data.recipients || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    if (recipientsOpen) loadRecipients(recipientQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientsOpen]);

  const saveRecipient = async () => {
    if (!recipientDraft?.name || !recipientDraft?.street1 || !recipientDraft?.city || !recipientDraft?.state || !recipientDraft?.zip) {
      return toast.error("Fill name, street, city, state, zip");
    }
    try {
      await api.post("/dropship/recipients", recipientDraft);
      toast.success("Recipient saved");
      setRecipientDraft(null);
      loadRecipients(recipientQ);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteRecipient = async (rid) => {
    try {
      await api.delete(`/dropship/recipients/${rid}`);
      toast.success("Recipient removed");
      loadRecipients(recipientQ);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const loadAdjustments = async (days = 30) => {
    try {
      const { data } = await api.get(`/dropship/fulfillment/carrier-adjustments?days=${days}`);
      setAdjustments(data);
    } catch { setAdjustments({ adjustments: [], count: 0, total_amount: 0 }); }
  };
  useEffect(() => { if (adjustmentsOpen) loadAdjustments(30); }, [adjustmentsOpen]);

  const openRateCompare = (oid = "") => {
    setRateCompareRates([]);
    setRateCompareOid(oid || (readyOrders[0]?.id || ""));
    setRateCompareOpen(true);
  };
  const runRateCompare = async () => {
    if (!rateCompareOid) return toast.error("Pick an order first");
    setRateCompareBusy(true); setRateCompareRates([]);
    try {
      const { data } = await api.post(`/dropship/orders/${rateCompareOid}/rates`, {
        weight_lb: Number(weight) || 1,
        length_in: settings.default_length_in,
        width_in:  settings.default_width_in,
        height_in: settings.default_height_in,
      });
      setRateCompareRates(data.rates || []);
      if (!(data.rates || []).length) toast.error("No rates returned by carriers");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rate shop failed");
    } finally { setRateCompareBusy(false); }
  };

  const requestQrReturn = async () => {
    if (!qrReturnRow?.id) return;
    const txId = qrReturnRow.shippo_transaction_id || qrReturnRow.transaction_id;
    if (!txId) {
      // fall back: server also accepts if we have the original — try anyway.
    }
    setQrReturnBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${qrReturnRow.id}/qr-return`, {
        original_transaction_id: txId || qrReturnRow.id,
        include_qr: true,
      });
      if (data.qr_code_url) {
        window.open(data.qr_code_url, "_blank");
        toast.success(`QR return ready · ${data.tracking || ""}`);
      } else {
        toast.error("QR return created but no URL returned");
      }
      setQrReturnRow(null);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "QR return failed");
    } finally { setQrReturnBusy(false); }
  };

  const submitSla = async () => {
    if (selected.size === 0) return toast.error("Select ready orders first");
    if (!slaShipBy && !slaHoldUntil) return toast.error("Set ship-by or hold-until");
    setSlaBusy(true);
    try {
      const { data } = await api.post("/dropship/orders/sla", {
        order_ids: Array.from(selected),
        ship_by:   slaShipBy || null,
        hold_until: slaHoldUntil || null,
      });
      toast.success(`SLA applied to ${data.modified} order${data.modified === 1 ? "" : "s"}`);
      setSlaOpen(false);
      setSlaShipBy(""); setSlaHoldUntil("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply SLA");
    } finally { setSlaBusy(false); }
  };

  const submitBulkVoid = async () => {
    if (voidSelected.size === 0) return toast.error("Select shipments to void");
    setBulkVoidBusy(true); setBulkVoidResult(null);
    try {
      const { data } = await api.post("/dropship/orders/bulk-void", {
        order_ids: Array.from(voidSelected),
      });
      setBulkVoidResult(data);
      const okCount = (data.results || []).filter(r => r.ok).length;
      toast.success(`Void requested · ${okCount}/${data.attempted} succeeded`);
      loadHistory();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk void failed");
    } finally { setBulkVoidBusy(false); }
  };
  const toggleVoid = (id) => setVoidSelected(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  return (
    <div className="space-y-3">
      {/* Compact card header (Feb 2026 · Iter 198) · title + Postage
          balance on ONE line. Long description removed to free up the
          fold for the order table (see "Fulfillment Center" tooltip on
          the title for the same explainer). */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2
          className="text-[15px] font-semibold tracking-tight"
          title="Bulk-buy labels, print pack slips, and schedule carrier pickups for every ready-to-ship dropship order. All labels are Verqo-brokered — claim + insurance backing included."
        >
          Fulfillment Center
        </h2>
        <div className="flex items-center gap-2 shrink-0" data-testid="fulfillment-header-actions">
          {/* Global data ops · Rescan · Import · Export.
              Shopify pattern: top-right of the section card, separate from
              filter tiles. Only visible on the Orders sub-tab (where the
              live orders feed exists). Wired via `dailyActionsRef` — the
              DailyOrdersTab child publishes its internal functions there. */}
          {subTab === "orders" && (
            <>
              <button
                onClick={() => dailyActionsRef.current.rescan?.()}
                disabled={dailyRescanning}
                data-testid="fulfillment-rescan"
                title="Re-scan carrier tracking events for movement red-flags"
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${dailyRescanning ? "animate-spin" : ""}`}/>
                Rescan
              </button>
              <button
                onClick={() => dailyActionsRef.current.openCsvImport?.()}
                data-testid="fulfillment-import-tracking"
                title="Bulk-mark orders shipped with tracking numbers from a CSV"
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60"
              >
                <Upload className="w-3.5 h-3.5"/>Import
              </button>
              <button
                onClick={() => dailyActionsRef.current.exportCsv?.()}
                disabled={!orders || orders.length === 0}
                data-testid="fulfillment-export-csv"
                title={selected.size > 0 ? `Export ${selected.size} selected order${selected.size === 1 ? "" : "s"}` : "Export the currently filtered orders"}
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-3.5 h-3.5"/>Export{selected.size > 0 ? ` (${selected.size})` : ""}
              </button>
              <span className="w-px h-5 bg-border mx-1" />
            </>
          )}
          {/* Postage balance pill — click to see auto-refill + add funds.
              Always rendered (with a "Setup" state when balance unavailable). */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="fulfillment-postage-pill"
                title="Verqo Shipping postage balance — click to manage"
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border text-[12px] font-medium transition-colors ${
                  balance?.balance == null
                    ? "border-border bg-secondary/50 text-foreground/60 hover:bg-secondary"
                  : Number(balance.balance) < 10
                    ? "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100"
                  : Number(balance.balance) < 25
                    ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                }`}
              >
                <Wallet className="w-3.5 h-3.5"/>
                <span className="text-[10.5px] mono uppercase tracking-[0.14em] opacity-70">Postage</span>
                <span className="tabular-nums font-semibold">
                  {balance?.balance != null ? `$${Number(balance.balance).toFixed(2)}` : "Setup"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="postage-popover">
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Verqo postage wallet</div>
                  <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">Wallet</span>
                </div>
                {balance?.balance != null ? (
                  <>
                    <div className={`text-2xl font-bold tabular-nums mt-1 ${
                      Number(balance.balance) < 10 ? "text-rose-700"
                      : Number(balance.balance) < 25 ? "text-amber-700"
                      : "text-foreground"
                    }`}>
                      ${Number(balance.balance).toFixed(2)}
                      <span className="text-[11px] font-normal text-foreground/50 ml-1.5 uppercase tracking-[0.14em]">{balance.currency || "USD"}</span>
                    </div>
                    {Number(balance.balance) < 25 && (
                      <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5"/>
                        <div className="text-[11px] text-amber-900">
                          Low balance — label buys will be blocked. Top up to keep shipping.
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[13px] text-foreground/70 mt-1">Loading…</div>
                )}
              </div>

              {/* Top-up actions */}
              <div className="px-4 py-3 border-b border-border space-y-2">
                <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 mb-1">Top up postage</div>
                <button
                  onClick={() => { setPostageDialogOpen(true); setPostageTopupSource("wallet"); }}
                  data-testid="postage-topup-wallet"
                  className="w-full inline-flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-border bg-white hover:bg-secondary/60 text-[12.5px] font-medium"
                >
                  <span className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-indigo-600"/>
                    From main wallet
                  </span>
                  <span className="text-[11px] text-foreground/50 tabular-nums">
                    {balance?.main_wallet_balance != null ? `$${Number(balance.main_wallet_balance).toFixed(2)} avail` : "—"}
                  </span>
                </button>
                <button
                  onClick={() => { setPostageDialogOpen(true); setPostageTopupSource("card"); }}
                  data-testid="postage-topup-card"
                  className="w-full inline-flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-border bg-white hover:bg-secondary/60 text-[12.5px] font-medium"
                >
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-600"/>
                    From saved payment method
                  </span>
                  <ChevronRight className="w-3 h-3 text-foreground/40"/>
                </button>
              </div>

              {/* Auto-refill status + manage */}
              <div className="px-4 py-3 space-y-1.5 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="text-foreground/60">Auto-refill</span>
                  <span className="font-medium">
                    {balance?.auto_refill?.enabled
                      ? <span className="text-emerald-700">On · ${Number((balance.auto_refill.amount_cents || 0)/100).toFixed(0)} when &lt; ${Number((balance.auto_refill.threshold_cents || 0)/100).toFixed(0)}</span>
                      : <span className="text-foreground/50">Off</span>}
                  </span>
                </div>
                {balance?.checked_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Last checked</span>
                    <span className="tabular-nums text-foreground/70">
                      {(() => {
                        const secs = Math.floor((Date.now() - new Date(balance.checked_at).getTime()) / 1000);
                        if (secs < 60) return `${secs}s ago`;
                        if (secs < 3600) return `${Math.floor(secs/60)}m ago`;
                        return `${Math.floor(secs/3600)}h ago`;
                      })()}
                    </span>
                  </div>
                )}
              </div>
              <div className="px-4 py-3 border-t border-border">
                <button
                  onClick={() => setPostageDialogOpen(true)}
                  data-testid="postage-manage-btn"
                  className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-foreground text-background text-[12px] font-semibold hover:bg-foreground/90"
                >
                  Manage postage wallet
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── UI refactor · 3-tab sub-shell (Orders / Shipments / Settings) ── */}
      <div className="border-b-2 border-border flex items-end gap-0" data-testid="fulfillment-subtabs">
        {(() => {
          // Shipments badge · shipped/in-transit inflight count (post-ship
          // history). Movement red-flags now live on the Orders sub-tab
          // where the merged Daily Orders feed surfaces them.
          const inflight = (orders || []).filter(o =>
            ["shipped", "in_transit"].includes(o.status)).length;
          return [
            { k: "orders",    label: "Orders",    icon: ShoppingCart, badge: readyOrders.length, urgent: false },
            { k: "shipments", label: "Shipments", icon: Boxes,   badge: inflight, urgent: false },
            { k: "setup",     label: "Settings",  icon: SettingsIcon, badge: 0, urgent: false },
          ];
        })().map(t => {
          const Icon = t.icon;
          const active = subTab === t.k;
          return (
            <button key={t.k}
              onClick={() => setSubTab(t.k)}
              data-testid={`fulfillment-subtab-${t.k}`}
              className={`relative h-10 px-3.5 text-sm font-medium border-b-2 -mb-[1px] transition-all inline-flex items-center gap-2 group ${
                active
                  ? "border-indigo-600 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-secondary/40"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 transition-colors ${active ? "text-indigo-600" : "text-slate-400 group-hover:text-slate-700"}`}/>
              <span>{t.label}</span>
              {t.badge > 0 && (
                <span
                  data-testid={`fulfillment-subtab-${t.k}-badge`}
                  className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold tabular-nums ${
                    t.urgent
                      ? "bg-rose-100 text-rose-700"
                      : active
                        ? "bg-indigo-600 text-white"
                        : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Slim action bar — daily-driver actions grouped by frequency.
          Only shown on the Orders sub-tab.
          Order: Print labels → Print slips → Pick list → End of day → ⋯ More
          Scan demoted to ⋯ More → Tools → 'Barcode order lookup' — it's
          a keyboard-shortcut-with-a-laser for the subset of sellers who
          print barcoded pick slips, not a daily-driver.
          Rare actions (Manual label, Schedule pickup) also in ⋯ More.
          Configuration lives under the Setup sub-tab. */}
      {subTab === "orders" && (
        <div className="flex items-center gap-2 shrink-0 flex-wrap" data-testid="fulfillment-action-bar">
          <button
            onClick={printLabels}
            disabled={printBusy || selected.size === 0}
            data-testid="fulfillment-print-labels"
            title={selected.size === 0 ? "Select bought orders to print their labels" : `Print ${selected.size} label${selected.size === 1 ? "" : "s"}`}
            className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50"
          >
            <Printer className="w-3.5 h-3.5"/>
            Print labels{selected.size ? ` · ${selected.size}` : ""}
          </button>
          <button
            onClick={printSlips}
            disabled={printBusy || selected.size === 0}
            data-testid="fulfillment-print-slips"
            title={selected.size === 0 ? "Select orders to print their pack slips" : `Print ${selected.size} pack slip${selected.size === 1 ? "" : "s"}`}
            className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50"
          >
            <ClipboardList className="w-3.5 h-3.5"/>
            Print slips{selected.size ? ` · ${selected.size}` : ""}
          </button>
          <button
            onClick={printPickList}
            disabled={printBusy || selected.size === 0}
            data-testid="fulfillment-print-picklist"
            title={selected.size === 0 ? "Select orders to generate a warehouse pick list" : `Generate pick list for ${selected.size} order${selected.size === 1 ? "" : "s"}`}
            className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50"
          >
            <Boxes className="w-3.5 h-3.5"/>
            Pick list{selected.size ? ` · ${selected.size}` : ""}
          </button>
          <button
            onClick={() => setEodOpen(true)}
            data-testid="fulfillment-eod-open"
            title="End-of-day manifest, SCAN sheet, and carrier pickup"
            className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
          >
            <Sunset className="w-3.5 h-3.5"/>
            End of day{manifests.length ? ` · ${manifests.length}` : ""}
          </button>

          {/* ⋯ More — occasional/rare actions grouped by section header */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="fulfillment-more-menu"
                className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60"
              >
                <MoreHorizontal className="w-3.5 h-3.5"/>
                More
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {/* Selection-scoped actions — only visible/relevant when N selected */}
              {selected.size > 0 && (
                <>
                  <div className="px-2 py-1.5 text-[9.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                    Selection · {selected.size}
                  </div>
                  <DropdownMenuItem
                    onClick={printCombined}
                    disabled={printBusy}
                    data-testid="more-print-combined"
                  >
                    <Printer className="w-3.5 h-3.5 mr-2"/>Print labels + slips · {selected.size}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSlaOpen(true)}
                    data-testid="more-sla"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-2"/>Apply SLA · {selected.size}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      if (selected.size === 0) return;
                      if (!window.confirm(`Void & refund labels for ${selected.size} order${selected.size === 1 ? "" : "s"}? Only unscanned USPS labels within 28 days can be refunded. Postage wallet will be credited automatically.`)) return;
                      try {
                        const { data } = await api.post("/dropship/orders/bulk-void-refund",
                          { order_ids: Array.from(selected) });
                        const parts = [];
                        if (data.success?.length) parts.push(`${data.success.length} voided`);
                        if (data.credited_cents) parts.push(`credited $${(data.credited_cents/100).toFixed(2)}`);
                        if (data.already_refunded?.length) parts.push(`${data.already_refunded.length} already`);
                        if (data.skipped?.length) parts.push(`${data.skipped.length} skipped`);
                        if (data.errors?.length) parts.push(`${data.errors.length} errors`);
                        toast.success(`Bulk void done — ${parts.join(", ")}`);
                        setSelected(new Set());
                        await loadPostageWallet();
                        await reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Bulk void failed");
                      }
                    }}
                    data-testid="more-bulk-void"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-2"/>Void & refund · {selected.size}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator/>
                </>
              )}

              {/* Tools — one-offs, occasional */}
              <div className="px-2 py-1.5 text-[9.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                Tools
              </div>
              <DropdownMenuItem
                onClick={() => { setScanOpen(true); setScanCode(""); setScanResult(null); }}
                data-testid="more-barcode-lookup"
              >
                <Package className="w-3.5 h-3.5 mr-2"/>Barcode order lookup
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openRateCompare()}
                disabled={readyOrders.length === 0}
                data-testid="more-rate-compare"
              >
                <TrendingUp className="w-3.5 h-3.5 mr-2"/>Rate compare (ad-hoc)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setManualLabelOpen(true)}
                data-testid="more-manual-label"
              >
                <Plus className="w-3.5 h-3.5 mr-2"/>Manual label
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPickupOpen(true)}
                disabled={shippedVerqo.length === 0}
                data-testid="more-schedule-pickup"
              >
                <CalendarClock className="w-3.5 h-3.5 mr-2"/>Schedule pickup
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setParserOpen(true)}
                data-testid="more-parse-address"
              >
                <FileText className="w-3.5 h-3.5 mr-2"/>Parse address
              </DropdownMenuItem>
              <DropdownMenuSeparator/>

              {/* Reports & analytics */}
              <div className="px-2 py-1.5 text-[9.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                Reports
              </div>
              <DropdownMenuItem
                onClick={() => setReportsOpen(v => !v)}
                data-testid="more-reports"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-2"/>Reports
                {reportsOpen && <span className="ml-auto text-[10px] mono text-foreground/50">OPEN</span>}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setAdjustmentsOpen(v => !v)}
                data-testid="more-adjustments"
              >
                <DollarSign className="w-3.5 h-3.5 mr-2"/>Adjustments
                {adjustmentsOpen && <span className="ml-auto text-[10px] mono text-foreground/50">OPEN</span>}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPlaybooksOpen(true)}
                data-testid="more-playbooks"
              >
                <FileSignature className="w-3.5 h-3.5 mr-2"/>
                Playbooks{playbooks.length ? ` · ${playbooks.length}` : ""}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Setup Hub — chip-strip navigator that swaps the config panel below.
          Only shown on the Setup sub-tab. Each chip toggles one panel via its
          existing xxxOpen state (see effect above). */}
      {subTab === "setup" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="setup-hub">
          <div className="px-5 py-3 border-b border-border bg-secondary/40">
            <div className="text-[13px] font-medium">Setup</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              Configure everything that supports your fulfillment workflow — profiles, templates, SKU mapping, connectors, address book, automation, and adjustments.
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 px-4 py-3 border-b border-border">
            {[
              { k: "profiles",    label: "Profiles",       icon: Boxes,         hint: "Carrier defaults, insurance, dims" },
              { k: "templates",   label: "Slip templates", icon: FileSignature, hint: "Pack slip layouts" },
              { k: "skus",        label: "SKU map",        icon: FileText,      hint: "Buyer ↔ vendor SKU translation" },
              { k: "connectors",  label: "Connectors",     icon: Zap,           hint: "USPS · UPS · FedEx · 3PL" },
              { k: "address",     label: "Address book",   icon: Users,         hint: "Ship-from warehouses" },
              { k: "automation",  label: "Automation",     icon: Zap,           hint: `Rules${rules.length ? ` · ${rules.length}` : ""}` },
              { k: "playbooks",   label: "Playbooks",      icon: FileSignature, hint: `Saved shipping plans${playbooks.length ? ` · ${playbooks.length}` : ""}` },
            ].map(s => {
              const Icon = s.icon;
              const active = setupSection === s.k;
              return (
                <button
                  key={s.k}
                  onClick={() => {
                    setSetupSection(s.k);
                    if (s.k === "playbooks") setPlaybooksOpen(true);
                  }}
                  data-testid={`setup-section-${s.k}`}
                  title={s.hint}
                  className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-semibold border transition-colors ${
                    active
                      ? "bg-foreground text-background border-foreground shadow-sm"
                      : "bg-white text-foreground/70 border-border hover:bg-secondary/60 hover:border-foreground/30"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5"/>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Settings panel (Phase 2) ────────────────────────────────── */}
      {settingsOpen && (
        <FulfillmentSettingsPanel
          settings={settings}
          patchSettings={patchSettings}
          saveSettings={saveSettings}
          settingsBusy={settingsBusy}
          settingsDirty={settingsDirty}
          addWarehouse={addWarehouse}
          removeWarehouse={removeWarehouse}
          updateWarehouse={updateWarehouse}
          setDefaultWarehouse={setDefaultWarehouse}
          setParserOpen={setParserOpen}
          scale={scale}
        />
      )}


      {/* ── Reports panel (Phase 4 · gap-fill) ──────────────────────── */}
      {reportsOpen && (
        <ReportsPanel
          reports={reports}
          reportsDays={reportsDays}
          setReportsDays={setReportsDays}
        />
      )}

      {automationOpen && (
        <AutomationRulesPanel
          rules={rules}
          setRules={setRules}
          rulesMeta={rulesMeta}
          ruleDraft={ruleDraft}
          setRuleDraft={setRuleDraft}
          carrierAccounts={carrierAccounts}
          setCarrierAccounts={setCarrierAccounts}
          setCarrierDialog={setCarrierDialog}
          setTemplatesOpen={setTemplatesOpen}
          supportedCarriers={supportedCarriers}
        />
      )}

      <CarrierCredentialDialog
        carrierDialog={carrierDialog}
        setCarrierDialog={setCarrierDialog}
        setCarrierAccounts={setCarrierAccounts}
      />

      <RuleEditorDialog
        ruleDraft={ruleDraft}
        setRuleDraft={setRuleDraft}
        rules={rules}
        setRules={setRules}
        rulesMeta={rulesMeta}
        settings={settings}
        playbooks={playbooks}
      />

      {/* Option A · Playbooks manager sheet */}
      <PlaybooksSheet
        open={playbooksOpen}
        onOpenChange={setPlaybooksOpen}
        playbooks={playbooks}
        setPlaybooks={setPlaybooks}
        playbookDraft={playbookDraft}
        setPlaybookDraft={setPlaybookDraft}
        selected={selected}
        reload={reload}
        settings={settings}
        playbookBusy={playbookBusy}
        setPlaybookBusy={setPlaybookBusy}
      />


      {/* ── Queue chip strip · shared lens across Orders AND Shipments.
          Filter you set here follows you across sub-tabs (persistent
          activeQueueId). "Manage" opens the full Queues CRUD in a
          dialog. */}
      {/* ── Unified filter strip (queues + draft batches side-by-side on
          Orders; queues only on Shipments). Compacted from 2 rows into 1
          to reduce vertical chrome above the order table (Feb 2026 · Iter 197). */}
      {(subTab === "orders" || subTab === "shipments") && (
      <div className="flex items-center gap-4 flex-wrap py-2 -mt-1" data-testid="queue-draft-strip">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="queue-chip-strip">
          <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/45 mr-1">Queue</span>
          <button
            onClick={() => { setActiveQueueId(null); setActiveQueueOrderIds(null); }}
            data-testid="queue-chip-all"
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
              activeQueueId === null
                ? "bg-foreground text-background border-foreground"
                : "bg-white text-foreground/70 border-border hover:bg-secondary/60"
            }`}
          >
            All
            <span className="mono tabular-nums opacity-70">
              {(orders || []).filter(o => o.status === "forwarded" && !o.tracking).length}
            </span>
          </button>
          {queues.map(q => {
            const active = activeQueueId === q.id;
            const dot = ({
              indigo: "bg-indigo-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
              rose: "bg-rose-500", sky: "bg-sky-500", slate: "bg-slate-500",
            })[q.color || "indigo"];
            return (
              <button key={q.id}
                onClick={async () => {
                  if (active) {
                    setActiveQueueId(null); setActiveQueueOrderIds(null); return;
                  }
                  setQueueFilterBusy(true);
                  try {
                    const { data } = await api.get(`/dropship/order-queues/${q.id}/orders`);
                    const ids = new Set((data?.orders || []).map(o => o.id));
                    setActiveQueueOrderIds(ids);
                    setActiveQueueId(q.id);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not load queue");
                  } finally { setQueueFilterBusy(false); }
                }}
                data-testid={`queue-chip-${q.id}`}
                disabled={queueFilterBusy}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-white text-foreground/70 border-border hover:bg-secondary/60"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`}/>
                {q.name}
              </button>
            );
          })}
          <span className="text-foreground/25 mx-1">·</span>
          <button
            onClick={() => setQueuesDialogOpen(true)}
            data-testid="queue-chip-manage"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/60 hover:text-foreground hover:bg-secondary/60"
          >
            <Plus className="w-3 h-3"/> Manage queues
          </button>
          {activeQueueId && (
            <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/45" data-testid="queue-chip-active-hint">
              Filtered · queue applies across both tabs
            </span>
          )}
        </div>

        {/* Vertical divider between queue and draft groups (only shown on
            Orders sub-tab where draft chips also render). */}
        {subTab === "orders" && <div className="w-px h-6 bg-border" />}

        {/* Draft Batches chip strip · pre-purchase groupings on Orders.
            A draft batch is a set of orders you plan to ship together,
            created BEFORE labels are bought. When you buy labels for
            orders in a draft batch, the resulting shipment batch inherits
            the draft's name — giving you continuity between Orders and
            Shipments. Only shown on Orders (semantically pre-purchase). */}
        {subTab === "orders" && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="draft-batch-chip-strip">
          <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/45 mr-1">Draft batch</span>
          <button
            onClick={() => setActiveDraftBatchId(null)}
            data-testid="draft-batch-chip-all"
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
              activeDraftBatchId === null
                ? "bg-foreground text-background border-foreground"
                : "bg-white text-foreground/70 border-border hover:bg-secondary/60"
            }`}
          >
            All
          </button>
          {draftBatches.filter(d => !d.purchased_at).map(d => {
            const active = activeDraftBatchId === d.id;
            const dot = ({
              indigo: "bg-indigo-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
              rose: "bg-rose-500", sky: "bg-sky-500", slate: "bg-slate-500",
            })[d.color || "indigo"];
            const open = d.counts?.open ?? 0;
            const shipped = d.counts?.shipped ?? 0;
            return (
              <button key={d.id}
                onClick={() => setActiveDraftBatchId(active ? null : d.id)}
                data-testid={`draft-batch-chip-${d.id}`}
                title={d.notes || `${open} open · ${shipped} shipped`}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-white text-foreground/70 border-border hover:bg-secondary/60"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`}/>
                {d.name}
                <span className="mono tabular-nums opacity-70">{open}</span>
              </button>
            );
          })}
          <span className="text-foreground/25 mx-1">·</span>
          <button
            onClick={() => { setDraftBatchEditing({ name: "", color: "indigo" }); setDraftBatchDialogOpen(true); }}
            data-testid="draft-batch-new"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/60 hover:text-foreground hover:bg-secondary/60"
          >
            <Plus className="w-3 h-3"/> New draft
          </button>
          {activeDraftBatchId && (() => {
            const active = draftBatches.find(d => d.id === activeDraftBatchId);
            if (!active) return null;
            return (
              <>
                <button
                  onClick={async () => {
                    // Generate pick list for the entire draft batch — no
                    // manual selection needed. Warehouse-friendly one-tap.
                    if (!active.order_ids?.length) return toast.error("Draft is empty");
                    setPrintBusy(true);
                    try {
                      const { data } = await api.post("/dropship/fulfillment/pick-list",
                        { order_ids: active.order_ids });
                      const lines = data.lines || [];
                      if (lines.length === 0) return toast.error("No SKUs to pick");
                      openPrintWindow(
                        buildPickListHtml(data, active.name),
                        `Pick list · ${active.name}`,
                      );
                      toast.success(`Pick list ready · ${lines.length} SKUs · ${data.total_units} units`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Pick list failed");
                    } finally { setPrintBusy(false); }
                  }}
                  data-testid={`draft-batch-picklist-${active.id}`}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] mono uppercase tracking-[0.14em] text-indigo-700 hover:bg-indigo-50"
                >
                  <Printer className="w-3 h-3"/> Pick list · {active.order_ids?.length || 0}
                </button>
                <button
                  onClick={() => { setDraftBatchEditing(active); setDraftBatchDialogOpen(true); }}
                  data-testid={`draft-batch-edit-${active.id}`}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] mono uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground hover:bg-secondary/60"
                >
                  Edit · {active.name}
                </button>
                {active.counts?.shipped > 0 && (
                  <button
                    onClick={() => { setSubTab("shipments"); setShipmentsView("batches"); }}
                    data-testid={`draft-batch-view-shipped-${active.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] mono uppercase tracking-[0.14em] text-emerald-700 hover:bg-emerald-50"
                  >
                    View {active.counts.shipped} shipped in Shipments →
                  </button>
                )}
              </>
            );
          })()}
        </div>
        )}
      </div>
      )}

      {/* ── Batch action bar · contextual (was Batches sub-tab) ──
          Sits ABOVE the orders table so users see actions ready as
          soon as they select. Appears in Orders sub-tab whenever ≥1
          order is selected. Preflight → AI Optimize → Buy. */}
      {subTab === "orders" && selected.size > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-4 space-y-3" data-testid="batches-hero">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13px] font-semibold flex-1 min-w-0">
              {selected.size} order{selected.size === 1 ? "" : "s"} selected
              <span className="text-[11px] font-normal text-foreground/55 ml-2">· Preflight → Optimize → Buy</span>
            </div>
            <Input value={batchName} onChange={e => setBatchName(e.target.value)}
              placeholder="Batch name (optional)"
              data-testid="batches-name-input"
              className="h-9 w-48 text-[12px]"/>
            <button onClick={runPreflight} disabled={preflightBusy}
              data-testid="batches-preflight"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-3.5 text-[11.5px] mono uppercase tracking-[0.14em] border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
              {preflightBusy ? "Checking…" : `1 · Preflight ${selected.size}`}
            </button>
            <button onClick={runOptimize} disabled={optimizeBusy}
              data-testid="batches-optimize"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-3.5 text-[11.5px] mono uppercase tracking-[0.14em] border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-100 disabled:opacity-50">
              {optimizeBusy ? "Ranking…" : `2 · AI Optimize`}
            </button>
            <label className="flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/60">
              <input type="checkbox" checked={useAiOpt} onChange={e => setUseAiOpt(e.target.checked)}
                data-testid="batches-use-ai" className="w-3.5 h-3.5 accent-foreground"/>
              AI
            </label>
            <button onClick={bulkShip} disabled={busy}
              data-testid="batches-buy"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-3.5 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
              {busy ? "Buying…" : `3 · Buy ${selected.size} labels`}
            </button>
            <button onClick={clearRateCache}
              data-testid="batches-clear-cache"
              className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground underline">
              Clear cache
            </button>
            {/* Add selection to a draft batch (pre-purchase grouping) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="batches-add-to-draft"
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary"
                >
                  <Boxes className="w-3 h-3"/> Add to draft
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-[9.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                  Save {selected.size} to draft batch
                </div>
                {draftBatches.filter(d => !d.purchased_at).length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-foreground/55">No open drafts yet.</div>
                )}
                {draftBatches.filter(d => !d.purchased_at).map(d => (
                  <DropdownMenuItem key={d.id}
                    onClick={async () => {
                      try {
                        await api.post(`/dropship/draft-batches/${d.id}/add-orders`,
                          { order_ids: Array.from(selected) });
                        await loadDraftBatches();
                        toast.success(`Added ${selected.size} to "${d.name}"`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Add to draft failed");
                      }
                    }}
                    data-testid={`batches-add-to-draft-${d.id}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${
                      ({indigo:"bg-indigo-500",emerald:"bg-emerald-500",amber:"bg-amber-500",rose:"bg-rose-500",sky:"bg-sky-500",slate:"bg-slate-500"})[d.color || "indigo"]
                    }`}/>
                    {d.name}
                    <span className="ml-auto text-[10px] mono text-foreground/45">{d.counts?.open ?? 0}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator/>
                <DropdownMenuItem
                  onClick={() => { setDraftBatchEditing({ name: "", color: "indigo", order_ids: Array.from(selected) }); setDraftBatchDialogOpen(true); }}
                  data-testid="batches-new-draft-with-selection"
                >
                  <Plus className="w-3 h-3 mr-2"/> New draft with {selected.size} orders
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Categorizer row */}
          <div className="pt-2 border-t border-emerald-200/60">
            <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 mb-1.5">
              Auto-group into sub-batches
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={categorizeBy} onChange={e => setCategorizeBy(e.target.value)}
                data-testid="categorize-by"
                className="h-8 rounded-lg border border-border bg-white px-2 text-[11.5px]">
                <option value="zone">Zone (WEST / EAST / …)</option>
                <option value="state">State</option>
                <option value="supplier">Supplier</option>
                <option value="carrier">Carrier</option>
                <option value="warehouse">Warehouse</option>
                <option value="tag">First tag</option>
              </select>
              <button onClick={runCategorize} disabled={categorizeBusy}
                data-testid="categorize-run"
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary disabled:opacity-50">
                {categorizeBusy ? "Grouping…" : `Group ${selected.size} orders`}
              </button>
            </div>
            {categorizeResult && (
              <div className="mt-3 space-y-2" data-testid="categorize-results">
                {(categorizeResult.groups || []).map(g => (
                  <div key={g.key} data-testid={`categorize-group-${g.key}`}
                    className="rounded-lg border border-border bg-white p-2.5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{g.key}</span>
                      <span className="text-[12px] font-medium">{g.count} order{g.count === 1 ? "" : "s"}</span>
                      <span className="text-[11px] text-foreground/55 truncate">
                        {(g.sample || []).map(s => s.code).join(", ")}{g.count > 3 ? "…" : ""}
                      </span>
                    </div>
                    <button onClick={() => shipCategorizedGroup(g)}
                      data-testid={`categorize-ship-${g.key}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 bg-foreground text-background hover:bg-foreground/90">
                      Ship this group
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {preflightResults && (
            <div className="rounded-xl border border-border bg-white p-3" data-testid="preflight-results">
              <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/60 mb-2">Preflight results</div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[
                  ["ok", preflightResults.summary?.ok, "text-emerald-700"],
                  ["address_error", preflightResults.summary?.address_error, "text-rose-700"],
                  ["no_rates", preflightResults.summary?.no_rates, "text-amber-700"],
                  ["not_found", preflightResults.summary?.not_found, "text-foreground/60"],
                ].map(([k, v, cls]) => (
                  <div key={k} className={`rounded-lg border border-border p-2 text-center ${cls}`}>
                    <div className="text-[10.5px] mono uppercase tracking-[0.14em] opacity-70">{k.replace("_", " ")}</div>
                    <div className="text-[18px] font-semibold mono">{v || 0}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {(preflightResults.results || []).map((r) => (
                  <div key={r.order_id} className="text-[11.5px] flex items-center gap-2" data-testid={`preflight-row-${r.order_id}`}>
                    <span className={`w-2 h-2 rounded-full ${
                      r.status === "ok" ? "bg-emerald-500" :
                      r.status === "address_error" ? "bg-rose-500" :
                      r.status === "no_rates" ? "bg-amber-500" : "bg-neutral-300"
                    }`}/>
                    <span className="mono w-24 truncate">{r.code || r.order_id}</span>
                    <span className="text-foreground/60 truncate flex-1">
                      {r.status === "ok" && r.cheapest && (
                        <>{r.rate_count} rates · cheapest: <span className="mono">{r.cheapest.carrier} ${r.cheapest.amount?.toFixed(2)}</span> · {r.cheapest.days}d</>
                      )}
                      {r.status === "address_error" && <span className="text-rose-700">missing: {r.missing?.join(", ")}</span>}
                      {r.status === "no_rates" && <span className="text-amber-700">no carrier rates returned</span>}
                      {r.status === "not_found" && <span className="text-foreground/50">order not in your account</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {optimizeResults && (
            <div className="rounded-xl border border-border bg-white p-3" data-testid="optimize-results">
              <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/60 mb-2">
                Optimized picks (weights: cost {optimizeResults.weights?.cost} · speed {optimizeResults.weights?.speed} · reliability {optimizeResults.weights?.reliability})
              </div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {(optimizeResults.results || []).map((r) => r.recommended && (
                  <div key={r.order_id} className="text-[11.5px] flex items-center gap-2" data-testid={`optimize-row-${r.order_id}`}>
                    <span className="mono w-24 truncate">{r.code}</span>
                    <span className="mono text-emerald-700 w-32">{r.recommended.carrier} ${r.recommended.amount?.toFixed(2)}</span>
                    <span className="text-foreground/60">{r.recommended.days}d · score {r.recommended.score?.toFixed(1)}</span>
                    {r.recommended.reasoning && (
                      <span className="text-foreground/50 truncate" title={r.recommended.reasoning}> · {r.recommended.reasoning.slice(0, 60)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Shipments sub-tab · segmented toggle (labels · batches) ──
          Sits ABOVE the shipments panels — same principle as Orders:
          controls belong above the data they control. */}
      {subTab === "shipments" && (
        <div className="flex items-center gap-1 p-1 rounded-full border border-border bg-white w-fit" data-testid="shipments-view-toggle">
          {[
            { k: "labels",  label: "Individual labels" },
            { k: "batches", label: "Grouped by batch" },
          ].map(v => (
            <button key={v.k}
              onClick={() => setShipmentsView(v.k)}
              data-testid={`shipments-view-${v.k}`}
              className={`h-8 px-4 rounded-full text-[11.5px] font-medium transition-colors ${
                shipmentsView === v.k
                  ? "bg-foreground text-background"
                  : "text-foreground/60 hover:text-foreground"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* KPI strip removed on Orders sub-tab · replaced by the merged
          DailyOrdersTab's richer 5-tile movement-focused strip (Today ·
          In transit · Stalled · No movement · Pending pack). "Selected"
          count now lives inside the sticky bulk-action bar. "Ready to
          ship" count is still visible as the badge on the "All" queue
          chip below. */}
      {subTab !== "orders" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Ready to ship" value={readyOrders.length}/>
          <Stat label="Shipped via Verqo" value={shippedVerqo.length}/>
          <Stat label="Pickups scheduled" value={pickups.length}/>
          <Stat label="Selected" value={selected.size}/>
        </div>
      )}

      {/* ── Orders sub-tab · Unified live orders feed (Feb 2026 merge)
          Single table hosting the entire pre-ship + post-ship lifecycle.
          Owns: KPI strip (5-tile · movement red-flags) · filter chips
          (All · Today · In transit · No movement · Pending pack) · Import
          Tracking · Export CSV · Re-scan movement · search + Add filter ·
          third-party order IDs · per-row Ship-with-Verqo / Manual / Mark
          delivered / SLA request / Live Tracking dialog · checkbox
          multi-select. Selection is LIFTED to this parent so the top
          toolbar (Print labels · Print slips · Pick list · End of day)
          shares the same Set as the sticky bulk-action bar.
          `renderExtraBulkActions` injects the FULL bulk-buy controls
          (Profile picker · Weight input · live scale indicator · Buy
          Labels via Verqo Shipping) INTO DailyOrdersTab's own sticky selected
          bar — so there is exactly ONE bulk-action bar, not two. */}
      {subTab === "orders" && (
        <div data-testid="orders-unified-feed">
          <DailyOrdersTab
            orders={filteredOrdersForFeed}
            movement={movement}
            reload={reload}
            onRescan={onRescan}
            selectionSet={selected}
            onSelectionSetChange={setSelected}
            actionsRef={dailyActionsRef}
            onRescanningChange={setDailyRescanning}
            renderCustomerCell={(o) => {
              // Iter 246 · Compute queue + draft-batch chips for this row
              // so sellers can trace stuck orders back to the rule/group
              // that (should have) picked them up.
              const rowQueues = queueMembership[o.id] || [];
              const rowBatch  = draftBatchByOrder[o.id];
              return (
              /* HoverCard preview · restored from the legacy fulfillment
                 panel. Shows full ship-to · item · wholesale total · Compare
                 rates quick action. Portals to body so it escapes the row's
                 overflow-y-auto container. */
              <HoverCard openDelay={400} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="min-w-0 cursor-help" data-testid={`row-hover-trigger-${o.code}`}>
                    <div className="truncate font-medium underline decoration-dotted decoration-foreground/25 underline-offset-2">
                      {o.customer_name}
                    </div>
                    <div className="text-[11px] text-foreground/55 truncate flex items-center gap-1.5 flex-wrap">
                      <span className="truncate">{o.product_name}</span>
                      {o.tracking && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-border bg-white mono text-[10.5px]" title={o.tracking}>
                          <MapPin className="w-2.5 h-2.5 text-foreground/60"/>
                          {o.tracking}
                        </span>
                      )}
                    </div>
                    {/* Iter 246 · Queue + draft-batch membership chips. Rendered
                        only when the order actually belongs to a queue and/or
                        batch — clean rows for un-grouped orders. */}
                    {(rowQueues.length > 0 || rowBatch) && (
                      <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`row-membership-${o.code}`}>
                        {rowQueues.map(q => (
                          <button
                            key={q.id}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); applyQueueFilter(q.id); }}
                            title={`In queue · ${q.name} · click to filter`}
                            data-testid={`row-queue-chip-${o.code}-${q.id}`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] mono uppercase tracking-[0.14em] border transition-colors ${chipClass(q.color)}`}
                          >
                            <Inbox className="w-2.5 h-2.5"/>
                            {q.name}
                          </button>
                        ))}
                        {rowBatch && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setActiveDraftBatchId(rowBatch.id); }}
                            title={`In draft batch · ${rowBatch.name} · click to filter`}
                            data-testid={`row-batch-chip-${o.code}`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] mono uppercase tracking-[0.14em] border transition-colors ${chipClass(rowBatch.color)}`}
                          >
                            <Boxes className="w-2.5 h-2.5"/>
                            {rowBatch.name}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" sideOffset={12}
                  className="w-80 p-0" data-testid={`row-hover-card-${o.code}`}>
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2 pb-2 border-b border-border">
                      <div className="min-w-0">
                        <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">Order</div>
                        <div className="mono font-semibold text-[13px]">{o.code}</div>
                      </div>
                      <div className="text-right min-w-0">
                        <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">Buyer</div>
                        <div className="text-[11.5px] font-medium truncate max-w-[130px]">{o.buyer_name || "—"}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 mb-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3"/> Ship to
                      </div>
                      <div className="text-[12px] font-medium">{o.ship_to?.name || o.customer_name}</div>
                      {o.ship_to?.street1 ? (
                        <div className="text-[11.5px] text-foreground/70 leading-snug">
                          <div>{o.ship_to.street1}</div>
                          {o.ship_to.street2 && <div>{o.ship_to.street2}</div>}
                          <div className="mono">
                            {o.ship_to.city}, {o.ship_to.state} {o.ship_to.zip}
                          </div>
                          <div className="mono text-foreground/50">{o.ship_to.country || "US"}</div>
                        </div>
                      ) : (
                        <div className="text-[11.5px] text-amber-700 leading-snug">
                          {o.customer_address || "No structured address · check the order in the source store"}
                        </div>
                      )}
                    </div>
                    <div className="pt-2 border-t border-border">
                      <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 mb-1">Item</div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[12px] font-medium truncate">{o.product_name || "—"}</div>
                          <div className="text-[11px] text-foreground/55 mono">{o.product_id || o.sku || ""}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="mono text-[13px] font-bold">{o.qty}</div>
                          <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">qty</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-border text-[11.5px]">
                      <span className="text-foreground/60">Wholesale total</span>
                      <span className="mono font-semibold">
                        {money(Number(o.wholesale_cost || 0) * Number(o.qty || 0))}
                      </span>
                    </div>
                    {o.status === "forwarded" && !o.tracking && (
                      <button
                        onClick={() => openRateCompare(o.id)}
                        data-testid={`row-hover-compare-${o.code}`}
                        className="w-full text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center justify-center gap-1"
                      >
                        <TrendingUp className="w-3 h-3"/>
                        Compare rates
                      </button>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
              );
            }}
            renderRowActionsExtra={(o) => (
              /* RowShipMenu · portal-based Shadcn DropdownMenu with
                 Rate compare · Duplicate · Combine · Tag · Split · QR
                 return. Only rendered for ready-to-ship rows to avoid
                 clutter on shipped/delivered rows. */
              (o.status === "forwarded" && !o.tracking) ? (
                <RowShipMenu
                  order={o}
                  selected={selected}
                  setSelected={setSelected}
                  rowMenuOid={rowMenuOid}
                  setRowMenuOid={setRowMenuOid}
                  rowActionBusy={rowActionBusy}
                  setRowActionBusy={setRowActionBusy}
                  setRowTagOid={setRowTagOid}
                  setRowTagText={setRowTagText}
                  setRowSplitOid={setRowSplitOid}
                  setRowSplitParts={setRowSplitParts}
                  weight={weight}
                  reload={reload}
                  openRateCompare={openRateCompare}
                />
              ) : null
            )}
            renderExtraBulkActions={(_selOrders, _clear) => (
              <div className="flex items-center gap-3 flex-wrap" data-testid="fulfillment-bulk-controls">
                {settings.shipping_profiles.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] mono uppercase tracking-[0.14em] text-background/70">Profile</label>
                    <select value={selectedProfileId}
                      onChange={e => {
                        setSelectedProfileId(e.target.value);
                        const p = settings.shipping_profiles.find(x => x.id === e.target.value);
                        if (p) setWeight(p.weight_lb);
                      }}
                      data-testid="bulk-profile-picker"
                      className="h-8 rounded-lg border border-background/30 bg-background/10 text-background px-2 text-[11.5px]">
                      <option value="" className="text-foreground">Default</option>
                      {settings.shipping_profiles.map(p => (
                        <option key={p.id} value={p.id} className="text-foreground">
                          {p.name} · {p.weight_lb}lb · {p.length_in}×{p.width_in}×{p.height_in}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-center gap-2 relative">
                  <label className="text-[10px] mono uppercase tracking-[0.14em] text-background/70">Weight</label>
                  <div className="relative">
                    <Input type="number" min="0.1" step="0.1" max="150" value={weight}
                      onChange={e => setWeight(e.target.value)}
                      data-testid="fulfillment-weight"
                      className="w-20 h-8 mono pl-2 pr-6 text-right text-foreground bg-background text-[12px]"/>
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] mono text-foreground/55">lb</span>
                  </div>
                  {scale.paired && scale.weightLb != null && (
                    <span
                      data-testid="scale-live-indicator"
                      title={`Live from ${scale.deviceName || "scale"}${scale.stable ? " · stable" : ""}`}
                      className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] mono uppercase tracking-[0.14em] border ${
                        scale.stable
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-amber-300 bg-amber-50 text-amber-800 animate-pulse"
                      }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current inline-block"/>
                      {scale.weightLb.toFixed(2)}{scale.stable ? " ✓" : " …"}
                    </span>
                  )}
                </div>
                <button onClick={bulkShip} disabled={busy || selected.size === 0}
                  data-testid="fulfillment-bulk-ship"
                  className="inline-flex items-center gap-1.5 rounded-full h-8 px-4 text-[11px] mono uppercase tracking-[0.14em] bg-background text-foreground hover:bg-background/90 disabled:opacity-50">
                  <ShieldCheck className="w-3.5 h-3.5"/>
                  {busy ? "Buying…" : `Buy ${selected.size} label${selected.size === 1 ? "" : "s"}`}
                </button>
              </div>
            )}
          />
        </div>
      )}


      {/* Row action: tag prompt */}
      <RowTagDialog
        rowTagOid={rowTagOid}
        setRowTagOid={setRowTagOid}
        rowTagText={rowTagText}
        setRowTagText={setRowTagText}
        setRowActionBusy={setRowActionBusy}
        reload={reload}
      />

      {/* Row action: split shipment */}
      <RowSplitDialog
        rowSplitOid={rowSplitOid}
        setRowSplitOid={setRowSplitOid}
        rowSplitParts={rowSplitParts}
        setRowSplitParts={setRowSplitParts}
        orders={orders}
        setRowActionBusy={setRowActionBusy}
        reload={reload}
      />

      {/* Bulk result */}
      {bulkResult && (
        <div className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="fulfillment-result">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[13px] font-semibold">{bulkResult.purchased} of {bulkResult.requested} labels bought</div>
              <div className="text-[11.5px] text-foreground/60 mt-0.5">
                Total: {money(bulkResult.total_billed || 0)} · Cost: {money(bulkResult.total_cost || 0)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openLabels} disabled={bulkResult.purchased === 0}
                data-testid="fulfillment-open-labels"
                className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/60 disabled:opacity-50">
                <ExternalLink className="w-3.5 h-3.5"/>Open all labels
              </button>
              <button onClick={printPackSlips} disabled={bulkResult.purchased === 0}
                data-testid="fulfillment-print-packslips"
                className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
                <FileText className="w-3.5 h-3.5"/>Print pack slips
              </button>
            </div>
          </div>
          {bulkResult.results?.some(r => !r.ok) && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[11.5px]">
              <div className="font-medium text-rose-900 mb-1.5">{bulkResult.failed} failed</div>
              {bulkResult.results.filter(r => !r.ok).map((r, i) => (
                <div key={i} className="text-rose-800/85">
                  <span className="mono">{r.order_id.slice(-8)}</span> — {r.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shipped Verqo orders — quick return-label access */}
      {shippedVerqo.length > 0 && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="shipped-verqo-list">
          <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
            <div className="text-[13px] font-medium">Shipped via Verqo · return labels</div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  const ids = shippedVerqo.filter(o => o.label_url).map(o => o.id);
                  if (ids.length === 0) {
                    toast.error("No labels available to reprint");
                    return;
                  }
                  setReprintBusy(true);
                  try {
                    const { data } = await api.post("/dropship/fulfillment/reprint-batch", { order_ids: ids });
                    const w = window.open("", "_blank");
                    if (w) { w.document.write(data.html); w.document.close(); }
                    toast.success(`Reprinting ${data.count} labels`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not reprint");
                  } finally { setReprintBusy(false); }
                }}
                disabled={reprintBusy}
                data-testid="fulfillment-reprint-all"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1 disabled:opacity-50">
                <Package className="w-3 h-3"/>{reprintBusy ? "…" : "Reprint all"}
              </button>
              {/* Phase 6 · Ledger CSV export */}
              <button
                onClick={async () => {
                  setLedgerBusy(true);
                  try {
                    const resp = await api.get("/dropship/fulfillment/ledger.csv?days=90", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `verqo-shipping-ledger-${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Ledger downloaded");
                  } catch { toast.error("Could not export"); }
                  finally { setLedgerBusy(false); }
                }}
                disabled={ledgerBusy}
                data-testid="fulfillment-ledger-csv"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1 disabled:opacity-50">
                <FileText className="w-3 h-3"/>{ledgerBusy ? "…" : "Ledger CSV"}
              </button>
              {/* Print pack slips only (Phase 1) */}
              <button
                onClick={async () => {
                  const ids = shippedVerqo.map(o => o.id);
                  if (ids.length === 0) return;
                  try {
                    const { data } = await api.post("/dropship/fulfillment/pack-slips-batch", { order_ids: ids });
                    const w = window.open("", "_blank");
                    if (w) { w.document.write(data.html); w.document.close(); }
                    toast.success(`${data.count} pack slips`);
                  } catch { toast.error("Could not generate pack slips"); }
                }}
                data-testid="fulfillment-print-slips-all"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
                <FileText className="w-3 h-3"/>Pack slips
              </button>
              {/* Combined labels + pack slips */}
              <button
                onClick={async () => {
                  const ids = shippedVerqo.filter(o => o.label_url).map(o => o.id);
                  if (ids.length === 0) {
                    toast.error("No labels available");
                    return;
                  }
                  try {
                    const { data } = await api.post("/dropship/fulfillment/labels-with-slips", { order_ids: ids });
                    const w = window.open("", "_blank");
                    if (w) { w.document.write(data.html); w.document.close(); }
                    toast.success(`${data.count} label + slip pairs`);
                  } catch { toast.error("Could not generate combined print"); }
                }}
                data-testid="fulfillment-labels-with-slips"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 bg-foreground text-background hover:bg-foreground/90 inline-flex items-center gap-1">
                <Package className="w-3 h-3"/>Labels + slips
              </button>
              <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                {shippedVerqo.length} shipment{shippedVerqo.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {shippedVerqo.slice(0, 20).map(o => (
              <div key={o.id} className="grid grid-cols-[120px_1fr_100px_130px_100px_180px] gap-3 px-5 py-2.5 items-center text-[12.5px]"
                data-testid={`shipped-order-${o.code}`}>
                <div className="mono font-medium">{o.code}</div>
                <div className="min-w-0 truncate text-foreground/80">{o.customer_name}</div>
                <div className="text-foreground/60 truncate">{o.carrier}</div>
                <div className="mono text-[11px] text-foreground/60 truncate">{o.tracking}</div>
                {/* Tracking status pill — populated by Verqo Shipping webhook (Phase 4a) */}
                <div>
                  {o.tracking_status && (
                    <span className={`text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                      o.status === "delivered"    ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                      o.status === "returned"     ? "bg-amber-100 text-amber-800 border-amber-200"      :
                      o.tracking_status?.toUpperCase() === "TRANSIT" ? "bg-sky-100 text-sky-800 border-sky-200" :
                      "bg-secondary text-foreground/70 border-border"
                    }`} data-testid={`tracking-status-${o.code}`}>
                      {o.status === "delivered" ? "Delivered" : (o.tracking_status || "In transit")}
                    </span>
                  )}
                </div>
                <div className="flex justify-end gap-1">
                  {/* Phase 4c · Void & refund — only for unscanned labels */}
                  {!o.refund_id && ["TRANSIT","DELIVERED"].indexOf((o.tracking_status || "").toUpperCase()) === -1 && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Void the ${o.carrier} label for ${o.code}? Postage will be refunded within 21 days.`)) return;
                        try {
                          const { data } = await api.post(`/dropship/orders/${o.id}/void-refund-label`);
                          toast.success(`Refund ${data.status || "requested"}`);
                          reload?.();
                        } catch (e) {
                          const d = e.response?.data?.detail;
                          toast.error(typeof d === "string" ? d : d?.message || "Refund failed");
                        }
                      }}
                      data-testid={`void-refund-${o.code}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-2.5 text-rose-700 hover:bg-rose-50">
                      Void
                    </button>
                  )}
                  {o.refund_id && (
                    <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
                          data-testid={`refund-status-${o.code}`}>
                      {(o.refund_status || "queued").toLowerCase()}
                    </span>
                  )}
                  <button onClick={() => returnLabel(o)}
                    data-testid={`return-label-${o.code}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
                    {o.return_label_url ? (<><ExternalLink className="w-3 h-3"/>Return</>) : (<>Return</>)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pickup history */}
      {pickups.length > 0 && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/40 text-[13px] font-medium">Scheduled pickups</div>
          <div className="divide-y divide-border">
            {pickups.slice(0, 8).map(p => (
              <div key={p.id} className="px-5 py-2.5 flex items-center justify-between text-[12.5px]">
                <div className="flex items-center gap-3">
                  <span className="mono text-foreground/60">{p.pickup_date}</span>
                  <span className="uppercase mono text-[10.5px] tracking-[0.14em] rounded-full px-2 py-0.5 bg-secondary text-foreground/70 border border-border">
                    {p.carrier}
                  </span>
                  <span className="text-foreground/70">{p.start_time} – {p.end_time}</span>
                </div>
                <div className="text-[11px] mono text-foreground/60">
                  {p.transactions?.length || 0} label{(p.transactions?.length || 0) === 1 ? "" : "s"}
                  {p.confirmation && <> · <span className="text-foreground/80">{p.confirmation}</span></>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── End of Day dialog — replaces the always-visible EOD manifest
          panel. Grouped semantically as "End of Day": manifest / SCAN
          sheet generation + past manifests + carrier pickup shortcut. */}
      <Dialog open={eodOpen} onOpenChange={setEodOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="eod-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sunset className="w-5 h-5 text-amber-600"/> End of day
            </DialogTitle>
          </DialogHeader>
          <div className="text-[12px] text-foreground/60 -mt-2 mb-4">
            Close out today's shipping — generate the carrier manifest (SCAN sheet) and
            schedule tomorrow's pickup. Do this once per day, ideally after your last
            label is bought.
          </div>

          {/* 1 · Manifest / SCAN sheet */}
          <div className="rounded-xl border border-border bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between gap-3">
              <div>
                <div className="text-[12.5px] font-semibold">Manifest · SCAN sheet</div>
                <div className="text-[10.5px] text-foreground/55 mt-0.5">
                  Required by USPS for high-volume drop-offs. One sheet per carrier per day.
                </div>
              </div>
              <button
                onClick={async () => {
                  const defaultAcc = carrierAccounts.find(a => a.is_default) || carrierAccounts[0];
                  if (!defaultAcc?.shippo_object_id) {
                    toast.error("Connect your USPS/UPS carrier account in Setup → Automation → Your carriers first — manifests need to be tied to your account, not Verqo's shared one.");
                    return;
                  }
                  setManifestBusy(true);
                  try {
                    const { data } = await api.post("/dropship/fulfillment/manifests", {
                      carrier_account: defaultAcc.shippo_object_id,
                    });
                    toast.success(`Manifest ${data.status || "created"}`);
                    const list = await api.get("/dropship/fulfillment/manifests");
                    setManifests(list.data?.manifests || []);
                  } catch (e) {
                    toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Manifest failed");
                  } finally { setManifestBusy(false); }
                }}
                disabled={manifestBusy}
                data-testid="fulfillment-generate-manifest"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1 disabled:opacity-50">
                {manifestBusy ? "Generating…" : "Generate today's"}
              </button>
            </div>
            {manifests.length === 0 ? (
              <div className="p-5 text-center text-[11.5px] text-foreground/55" data-testid="manifests-empty">
                {carrierAccounts.length === 0 ? (
                  <>Connect your own USPS/UPS/FedEx account in <strong>Setup → Automation → Your carriers</strong> to enable end-of-day manifests. Verqo Shipping requires a carrier-specific SCAN form, so it must be tied to your account.</>
                ) : (
                  <>No manifests yet today. Generate one after you've bought your last label — Verqo Shipping groups all of today's shipments into a single SCAN sheet the driver signs at pickup.</>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border max-h-56 overflow-y-auto">
                {manifests.slice(0, 8).map(m => (
                  <div key={m.manifest_id} className="px-4 py-2 flex items-center justify-between text-[11.5px]" data-testid={`manifest-${m.manifest_id}`}>
                    <div className="flex items-center gap-2.5">
                      <span className="mono text-foreground/60">{m.shipment_date}</span>
                      <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-secondary text-foreground/70 border border-border">{m.status || "pending"}</span>
                    </div>
                    {m.manifest_url ? (
                      <a href={m.manifest_url} target="_blank" rel="noreferrer"
                         className="text-[10px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3"/>PDF
                      </a>
                    ) : (
                      <span className="text-[10px] mono text-foreground/55">generating…</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 2 · Carrier pickup — often the natural EOD ritual */}
          <div className="rounded-xl border border-border bg-white p-4 mt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[12.5px] font-semibold flex items-center gap-2">
                  <Truck className="w-4 h-4"/> Schedule carrier pickup
                </div>
                <div className="text-[10.5px] text-foreground/55 mt-0.5">
                  Book USPS/UPS/FedEx to pick up tomorrow's shipments at your warehouse.
                </div>
              </div>
              <button
                onClick={() => { setEodOpen(false); setPickupOpen(true); }}
                disabled={shippedVerqo.length === 0}
                data-testid="eod-schedule-pickup"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1 disabled:opacity-50">
                <CalendarClock className="w-3.5 h-3.5"/>
                Book pickup
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Address parser dialog (Phase 4j) ─────────────────────── */}
      {parserOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setParserOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" onClick={e => e.stopPropagation()} data-testid="address-parser-dialog">
            <div className="text-[15px] font-semibold">Parse address</div>
            <div className="text-[12px] text-foreground/60">
              Paste a free-text address (from an email, CSV, etc.) — we&apos;ll normalize it into structured fields.
            </div>
            <textarea
              value={parserText}
              onChange={e => setParserText(e.target.value)}
              placeholder="731 Market St #200, San Francisco, CA 94103"
              rows={3}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] font-mono"
              data-testid="parser-textarea"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setParserOpen(false)}
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Close
              </button>
              <button
                onClick={async () => {
                  setParserBusy(true);
                  try {
                    const { data } = await api.post("/dropship/address-parse", { query: parserText });
                    if (data?.ok && data.address) {
                      const a = data.address;
                      // Append as a new warehouse row
                      patchSettings(s => {
                        s.ship_from_addresses = [
                          ...(s.ship_from_addresses || []),
                          {
                            name:    "Parsed warehouse",
                            street1: a.address_line_1 || "",
                            street2: a.address_line_2 || "",
                            city:    a.city_locality || "",
                            state:   a.state_province || "",
                            zip:     a.postal_code || "",
                            country: a.country_code || "US",
                            is_default: (s.ship_from_addresses || []).length === 0,
                          },
                        ];
                        return s;
                      });
                      toast.success("Address parsed — review it above");
                      setParserOpen(false);
                      setParserText("");
                    } else {
                      toast.error("Could not parse this address");
                    }
                  } catch { toast.error("Parser unavailable"); }
                  finally { setParserBusy(false); }
                }}
                disabled={parserBusy || parserText.trim().length < 6}
                data-testid="parser-submit"
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
                {parserBusy ? "Parsing…" : "Parse & add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Scan-to-print modal (Phase 5) ─────────────────────────── */}
      {scanOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setScanOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="scan-dialog">
            <div>
              <div className="text-[15px] font-semibold">Scan barcode</div>
              <div className="text-[12px] text-foreground/60 mt-1">
                Scan an order code, tracking #, or SKU. USB scanners just type + Enter.
              </div>
            </div>
            <Input
              autoFocus
              value={scanCode}
              onChange={e => setScanCode(e.target.value)}
              onKeyDown={async e => {
                if (e.key !== "Enter" || !scanCode.trim() || scanBusy) return;
                setScanBusy(true);
                try {
                  const { data } = await api.post("/dropship/fulfillment/scan", { code: scanCode.trim() });
                  setScanResult(data);
                  setScanCode("");
                  if (data.match === "order" && data.next === "ship") {
                    toast.success(`Order ${data.order.code} — ready to ship`);
                  } else if (data.match === "sku") {
                    toast.success(`${data.orders.length} orders match SKU ${data.sku}`);
                  } else if (data.match === "tracking") {
                    toast.success(`Tracking match — ${data.order.code}`);
                  } else {
                    toast.error("No match");
                  }
                } catch { toast.error("Scan failed"); }
                finally { setScanBusy(false); }
              }}
              placeholder="Waiting for scanner…"
              data-testid="scan-input"
              className="h-12 text-[14px] font-mono"
            />
            {scanResult && (
              <div className="rounded-xl border border-border p-3 bg-secondary/30 text-[12.5px]" data-testid="scan-result">
                {scanResult.match === "order" && (
                  <>
                    <div className="font-medium mono">{scanResult.order.code} · {scanResult.order.customer_name}</div>
                    <div className="text-foreground/60 mt-1">
                      Status: {scanResult.order.status} · Destination: {scanResult.order.ship_to?.city}, {scanResult.order.ship_to?.state}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => {
                          setScanOpen(false);
                          // Trigger the existing ship-with-verqo flow for this order.
                          document.querySelector(`[data-testid="ship-with-verqo-${scanResult.order.code}"]`)?.click();
                        }}
                        className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 bg-foreground text-background">
                        Ship now
                      </button>
                    </div>
                  </>
                )}
                {scanResult.match === "sku" && (
                  <div>
                    <div className="font-medium">{scanResult.orders.length} orders match SKU {scanResult.sku}</div>
                    <div className="mt-1 text-foreground/60">
                      {scanResult.orders.slice(0, 5).map(o => o.code).join(", ")}
                      {scanResult.orders.length > 5 ? ` +${scanResult.orders.length - 5} more` : ""}
                    </div>
                  </div>
                )}
                {scanResult.match === null && (
                  <div className="text-rose-700">No match found for &quot;{scanResult.code}&quot;</div>
                )}
              </div>
            )}
            <div className="flex items-center justify-end">
              <button onClick={() => setScanOpen(false)}
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manual create-a-label modal (Phase 6) ─────────────────── */}
      {manualLabelOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setManualLabelOpen(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="manual-label-dialog">
            <div>
              <div className="text-[15px] font-semibold">Quote & ship without an order</div>
              <div className="text-[12px] text-foreground/60 mt-1">
                Enter recipient + weight, get live carrier quotes, then commit to a shipment. Creates a stub order ready to ship with Verqo.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Recipient name" value={manualForm.to_name}
                onChange={e => setManualForm(f => ({ ...f, to_name: e.target.value }))}
                data-testid="manual-to-name" className="h-10 col-span-2"/>
              <Input placeholder="Street address" value={manualForm.to_street1}
                onChange={e => setManualForm(f => ({ ...f, to_street1: e.target.value }))}
                data-testid="manual-street1" className="h-10 col-span-2"/>
              <Input placeholder="City" value={manualForm.to_city}
                onChange={e => setManualForm(f => ({ ...f, to_city: e.target.value }))}
                data-testid="manual-city" className="h-10"/>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="State" value={manualForm.to_state}
                  onChange={e => setManualForm(f => ({ ...f, to_state: e.target.value.toUpperCase() }))}
                  data-testid="manual-state" className="h-10 mono"/>
                <Input placeholder="ZIP" value={manualForm.to_zip}
                  onChange={e => setManualForm(f => ({ ...f, to_zip: e.target.value }))}
                  data-testid="manual-zip" className="h-10 mono"/>
              </div>
              <Input type="number" min="0.1" step="0.1" placeholder="Weight (lb)"
                value={manualForm.weight_lb}
                onChange={e => setManualForm(f => ({ ...f, weight_lb: Number(e.target.value || 1) }))}
                data-testid="manual-weight" className="h-10 mono"/>
              <Input placeholder="Reference (optional)"
                value={manualForm.reference}
                onChange={e => setManualForm(f => ({ ...f, reference: e.target.value }))}
                data-testid="manual-reference"
                className="h-10"/>
            </div>

            {/* Live carrier quotes — turns Manual label into a proper
                quote → buy flow. Reuses the /rates-at-checkout/live-rates
                endpoint that already exists for merchant checkout widgets. */}
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/55">
                  Compare carrier rates
                </div>
                <button
                  onClick={async () => {
                    const f = manualForm;
                    if (!f.to_name || !f.to_street1 || !f.to_zip || !f.weight_lb) {
                      return toast.error("Fill recipient + address + weight first");
                    }
                    const ship_from = (settings?.ship_from_addresses || []).find(a => a.is_default)
                      || (settings?.ship_from_addresses || [])[0];
                    if (!ship_from) {
                      return toast.error("Add a ship-from address in Setup → Ship-from first");
                    }
                    setManualQuoteBusy(true); setManualQuotes([]);
                    try {
                      const { data } = await api.post("/dropship/rates-at-checkout/live-rates", {
                        ship_from: {
                          name: ship_from.name || "Warehouse", street1: ship_from.street1,
                          city: ship_from.city, state: ship_from.state, zip: ship_from.zip,
                          country: ship_from.country || "US", phone: ship_from.phone || "",
                        },
                        ship_to: {
                          name: f.to_name, street1: f.to_street1, city: f.to_city,
                          state: f.to_state, zip: f.to_zip, country: f.to_country || "US",
                        },
                        parcels: [{
                          length: settings?.default_length_in || 10,
                          width:  settings?.default_width_in  || 8,
                          height: settings?.default_height_in || 4,
                          distance_unit: "in",
                          weight: Number(f.weight_lb) || 1,
                          mass_unit: "lb",
                        }],
                      });
                      setManualQuotes(data.rates || []);
                      if ((data.rates || []).length === 0) {
                        toast.error("No rates returned — check address");
                      } else {
                        toast.success(`${data.count} rate${data.count === 1 ? "" : "s"} loaded`);
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Rate lookup failed");
                    } finally { setManualQuoteBusy(false); }
                  }}
                  disabled={manualQuoteBusy}
                  data-testid="manual-get-quotes"
                  className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <TrendingUp className="w-3 h-3"/>
                  {manualQuoteBusy ? "Fetching…" : "Get quotes"}
                </button>
              </div>
              {manualQuotes.length > 0 && (
                <div className="rounded-lg border border-border overflow-hidden" data-testid="manual-quotes-table">
                  <table className="w-full text-[11.5px]">
                    <thead className="bg-secondary/40 text-[9.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                      <tr>
                        <th className="text-left px-2 py-1.5">Carrier</th>
                        <th className="text-left px-2 py-1.5">Service</th>
                        <th className="text-right px-2 py-1.5">Days</th>
                        <th className="text-right px-2 py-1.5">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {[...manualQuotes].sort((a, b) => (a.amount || 0) - (b.amount || 0)).map((r, i) => (
                        <tr key={i} className={i === 0 ? "bg-emerald-50/60" : ""} data-testid={`manual-quote-row-${i}`}>
                          <td className="px-2 py-1.5 mono uppercase">{(() => {
                            const p = (r.provider || "").toLowerCase();
                            if (p === "warp" || p === "verqo-shipping") return "VERQO";
                            return r.provider || "—";
                          })()}</td>
                          <td className="px-2 py-1.5">{r.service || "—"}</td>
                          <td className="px-2 py-1.5 text-right mono">{r.days ?? "—"}</td>
                          <td className="px-2 py-1.5 text-right mono font-medium">${Number(r.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setManualLabelOpen(false)}
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!manualForm.to_name || !manualForm.to_street1 || !manualForm.to_zip) {
                    toast.error("Fill name, address, and ZIP");
                    return;
                  }
                  try {
                    const { data } = await api.post("/dropship/manual-shipments", manualForm);
                    setManualLabelOpen(false);
                    setManualQuotes([]);
                    toast.success(`Created ${data.code} — ready to ship`);
                    reload?.();
                    setManualForm({ to_name:"", to_street1:"", to_city:"", to_state:"", to_zip:"", to_country:"US", weight_lb: 1, reference: "" });
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not create");
                  }
                }}
                data-testid="manual-label-submit"
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
                Create & prep for shipping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notification templates panel (Phase 5) ─────────────────── */}
      {templatesOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setTemplatesOpen(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="templates-dialog">
            <div>
              <div className="text-[15px] font-semibold">Branded shipping notifications</div>
              <div className="text-[12px] text-foreground/60 mt-1">
                Templates fire automatically when a tracking status changes. Use <span className="mono">{"{code}"}</span>, <span className="mono">{"{tracking}"}</span>, <span className="mono">{"{carrier}"}</span>, <span className="mono">{"{customer_name}"}</span>, <span className="mono">{"{tracking_url}"}</span>.
              </div>
            </div>
            <div className="space-y-4">
              {Object.entries(templateEvents).map(([evtKey, label]) => {
                const t = templates.find(x => x.event === evtKey) || { event: evtKey, channel: "email", subject: "", body: "", active: true };
                return (
                  <div key={evtKey} className="rounded-xl border border-border p-4 space-y-2" data-testid={`template-${evtKey}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[13px] font-medium">{label}</div>
                      <label className="text-[10.5px] mono uppercase tracking-[0.14em] inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={t.active}
                          onChange={e => setTemplates(list => {
                            const others = list.filter(x => x.event !== evtKey);
                            return [...others, { ...t, active: e.target.checked }];
                          })}
                          className="w-3.5 h-3.5 accent-foreground"/>
                        active
                      </label>
                    </div>
                    <Input placeholder="Subject line" value={t.subject || ""}
                      onChange={e => setTemplates(list => {
                        const others = list.filter(x => x.event !== evtKey);
                        return [...others, { ...t, subject: e.target.value }];
                      })}
                      className="h-9"/>
                    <textarea placeholder="Message body — use {code}, {tracking}, {tracking_url}, {customer_name}"
                      value={t.body || ""}
                      onChange={e => setTemplates(list => {
                        const others = list.filter(x => x.event !== evtKey);
                        return [...others, { ...t, body: e.target.value }];
                      })}
                      rows={3}
                      data-testid={`template-body-${evtKey}`}
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[12.5px] font-mono"/>
                    <div className="flex items-center justify-end">
                      <button
                        onClick={async () => {
                          try {
                            await api.put(`/dropship/notification-templates/${evtKey}`, {
                              event: evtKey,
                              channel: t.channel || "email",
                              subject: t.subject || null,
                              body: t.body || "",
                              active: t.active !== false,
                            });
                            toast.success(`${label} template saved`);
                          } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
                        }}
                        data-testid={`template-save-${evtKey}`}
                        className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">
                        Save template
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-end pt-2 border-t border-border">
              <button onClick={() => setTemplatesOpen(false)}
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Schedule pickup dialog */}
      <Dialog open={pickupOpen} onOpenChange={v => !v && !pickupBusy && setPickupOpen(false)}>
        <DialogContent className="max-w-md" data-testid="fulfillment-pickup-dialog">
          <DialogHeader><DialogTitle>Schedule carrier pickup</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/60 -mt-1">
            Carrier will pick up all Verqo-shipped, unmoved labels from your default ship-from address. Currently supported: USPS &amp; DHL Express.
          </p>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Carrier</label>
                <select value={pickupCarrier} onChange={e => setPickupCarrier(e.target.value)}
                  data-testid="pickup-carrier"
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-[12.5px]">
                  <option value="usps">USPS</option>
                  <option value="dhl_express">DHL Express</option>
                </select>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Date</label>
                <Input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)}
                  data-testid="pickup-date"
                  className="h-10"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">From</label>
                <Input type="time" value={pickupWindow[0]}
                  onChange={e => setPickupWindow([e.target.value, pickupWindow[1]])}
                  data-testid="pickup-start" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">To</label>
                <Input type="time" value={pickupWindow[1]}
                  onChange={e => setPickupWindow([pickupWindow[0], e.target.value])}
                  data-testid="pickup-end" className="h-10"/>
              </div>
            </div>
          </div>
          <Button onClick={submitPickup} disabled={pickupBusy}
            data-testid="pickup-confirm"
            className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
            {pickupBusy ? "Scheduling…" : "Schedule pickup"}
          </Button>
        </DialogContent>
      </Dialog>


      {/* ── Package E4 · 3PL templates panel (only when Connectors chip is selected) ── */}
      {subTab === "setup" && setupSection === "connectors" && tplList.length > 0 && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="tpl-3pl-panel">
          <div className="px-5 py-3 border-b border-border bg-secondary/40">
            <div className="text-[13px] font-medium">3PL quick-install</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              Pre-configured connector templates for major 3PLs. Paste your API key → auto-wired as a `custom_api` connector.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {tplList.map(t => (
              <div key={t.id} data-testid={`tpl-3pl-${t.id}`} className="rounded-xl border border-border p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {t.logo ? <img src={t.logo} alt={t.name} className="w-6 h-6 rounded"/> : <Boxes className="w-5 h-5"/>}
                  <div className="font-medium text-[13px]">{t.name}</div>
                </div>
                <div className="text-[11.5px] text-foreground/60 min-h-[3em]">{t.description}</div>
                <div className="flex items-center justify-between">
                  <a href={t.docs_url} target="_blank" rel="noreferrer"
                    className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground underline">API docs</a>
                  <button onClick={() => install3PL(t.id)}
                    data-testid={`tpl-3pl-install-${t.id}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100">
                    Install
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Package A · Batches history panel · shown in Shipments tab (grouped view) ── */}
      {(batchesOpen || (subTab === "shipments" && shipmentsView === "batches")) && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-batches-panel">
          <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-medium">Batches</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Every bulk-buy of labels, grouped. Click &ldquo;Details&rdquo; to see the individual orders.
              </div>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90, 180].map(d => (
                <button key={d}
                  onClick={() => setBatchesDays(d)}
                  data-testid={`batches-days-${d}`}
                  className={`text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-2.5 border ${
                    batchesDays === d
                      ? "bg-foreground text-background border-foreground"
                      : "border-border bg-white hover:bg-secondary"
                  }`}>{d}d</button>
              ))}
              <button onClick={loadBatches} disabled={batchesBusy}
                data-testid="batches-refresh"
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary disabled:opacity-60">
                <RefreshCw className={`w-3 h-3 ${batchesBusy ? "animate-spin" : ""}`}/> Refresh
              </button>
            </div>
          </div>
          {batchesBusy ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">Loading…</div>
          ) : batches.length === 0 ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">
              No batches in the last {batchesDays} days. Run a bulk-buy to create one.
            </div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="bg-secondary/30 text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                <tr>
                  <th className="text-left px-3 py-2">Batch</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-right px-3 py-2">Requested</th>
                  <th className="text-right px-3 py-2">Purchased</th>
                  <th className="text-right px-3 py-2">Failed</th>
                  <th className="text-right px-3 py-2">Cost</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {batches.map(b => (
                  <tr key={b.id} data-testid={`batch-row-${b.id}`}>
                    <td className="px-3 py-2 mono text-[11px]">{(b.id || "").slice(0, 12)}…</td>
                    <td className="px-3 py-2 text-[12px]">{b.name || <span className="text-foreground/40 italic">unnamed</span>}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] bg-secondary text-foreground/70">
                        {b.source === "shippo_native" ? "Verqo Native" : "Verqo bulk"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right mono">{b.requested}</td>
                    <td className="px-3 py-2 text-right mono text-emerald-700">{b.purchased}</td>
                    <td className="px-3 py-2 text-right mono text-rose-700">{b.failed || 0}</td>
                    <td className="px-3 py-2 text-right mono">${Number(b.total_cost || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 mono text-[11px] text-foreground/60">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-right">
                      {b.source !== "shippo_native" && (
                        <button onClick={() => renameBatch(b.id, b.name)}
                          data-testid={`batch-rename-${b.id}`}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/60 hover:text-foreground underline">Rename</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Package B · Pack-slip templates panel ──────────────────────── */}
      {templatesOpenB && (
        <SlipTemplatesPanel
          templatesList={templatesList}
          templateDraft={templateDraft}
          setTemplateDraft={setTemplateDraft}
          saveTemplateB={saveTemplateB}
          deleteTemplateB={deleteTemplateB}
          openTemplateEditor={openTemplateEditor}
          previewTemplateB={previewTemplateB}
          settings={settings}
        />
      )}

      {/* ── Package B · Template editor dialog ─────────────────────────── */}
      <TemplateEditorDialog
        templateDraft={templateDraft}
        setTemplateDraft={setTemplateDraft}
        saveTemplateB={saveTemplateB}
        settings={settings}
      />

      {/* ── Package B · Vendor SKU map panel ───────────────────────────── */}
      {skusOpen && (
        <VendorSkusPanel
          skusList={skusList}
          skuDraft={skuDraft}
          setSkuDraft={setSkuDraft}
          skusQuery={skusQuery}
          setSkusQuery={setSkusQuery}
          saveSkuB={saveSkuB}
          deleteSkuB={deleteSkuB}
          loadSkusB={loadSkusB}
          importSkusCsvB={importSkusCsvB}
          skusCsvBusy={skusCsvBusy}
        />
      )}

      {/* ── Package B · SKU editor dialog ──────────────────────────────── */}
      <SkuEditorDialog
        skuDraft={skuDraft}
        setSkuDraft={setSkuDraft}
        saveSkuB={saveSkuB}
      />

      {/* ── Package C · Connectors panel ───────────────────────────────── */}
      {connectorsOpen && (
        <ConnectorsPanel
          connectors={connectors}
          connectorMeta={connectorMeta}
          connectorEvents={connectorEvents}
          setConnectorDraft={setConnectorDraft}
          testConnector={testConnector}
          deleteConnector={deleteConnector}
          showConnectorEvents={showConnectorEvents}
          emptyConnectorDraft={emptyConnectorDraft}
          syncConnector={syncConnector}
          emitTestEvent={emitTestEvent}
        />
      )}

      {/* ── Package C · Connector editor dialog ────────────────────────── */}
      <ConnectorEditorDialog
        connectorDraft={connectorDraft}
        setConnectorDraft={setConnectorDraft}
        saveConnector={saveConnector}
        connectorMeta={connectorMeta}
        connectorBusy={connectorBusy}
      />

      {/* ── Phase 8 · Shipment history panel · shown in Shipments tab (labels view) ── */}
      {(historyOpen || (subTab === "shipments" && shipmentsView === "labels")) && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-history-panel">          <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-medium">Shipment history</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Searchable log of every Verqo-brokered shipment. QR returns & void live here.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input placeholder="code / tracking / customer"
                value={historyFilters.q}
                onChange={e => setHistoryFilters({ ...historyFilters, q: e.target.value })}
                data-testid="history-q" className="h-9 w-56 text-[12.5px]"/>
              <select value={historyFilters.carrier}
                onChange={e => setHistoryFilters({ ...historyFilters, carrier: e.target.value })}
                data-testid="history-carrier"
                className="h-9 rounded-lg border border-border bg-white px-2 text-[12px]">
                <option value="">All carriers</option>
                <option value="usps">USPS</option>
                <option value="ups">UPS</option>
                <option value="fedex">FedEx</option>
                <option value="dhl">DHL</option>
              </select>
              <select value={historyFilters.status}
                onChange={e => setHistoryFilters({ ...historyFilters, status: e.target.value })}
                data-testid="history-status"
                className="h-9 rounded-lg border border-border bg-white px-2 text-[12px]">
                <option value="">All statuses</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="returned">Returned</option>
              </select>
              {[7, 30, 90, 180].map(d => (
                <button key={d}
                  onClick={() => setHistoryFilters({ ...historyFilters, days: d })}
                  data-testid={`history-days-${d}`}
                  className={`text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-2.5 border ${
                    historyFilters.days === d
                      ? "bg-foreground text-background border-foreground"
                      : "border-border bg-white hover:bg-secondary"
                  }`}>{d}d</button>
              ))}
              <button onClick={loadHistory} disabled={historyBusy}
                data-testid="history-refresh"
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary disabled:opacity-60">
                <RefreshCw className={`w-3 h-3 ${historyBusy ? "animate-spin" : ""}`}/> Refresh
              </button>
              {voidSelected.size > 0 && (
                <button onClick={submitBulkVoid} disabled={bulkVoidBusy}
                  data-testid="history-bulk-void"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] mono uppercase tracking-[0.14em] border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                  <AlertTriangle className="w-3 h-3"/> Void {voidSelected.size}
                </button>
              )}
            </div>
          </div>
          {historyBusy ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">Loading…</div>
          ) : history.length === 0 ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">
              No shipments in the last {historyFilters.days} days.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-secondary/30 text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                  <tr>
                    <th className="text-left px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Carrier</th>
                    <th className="text-left px-3 py-2">Tracking</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Cost</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {history.map(s => (
                    <tr key={s.id} data-testid={`history-row-${s.id}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox"
                          checked={voidSelected.has(s.id)}
                          onChange={() => toggleVoid(s.id)}
                          data-testid={`history-void-cb-${s.id}`}
                          disabled={!!s.refund_id || ["delivered","returned"].includes((s.tracking_status || "").toLowerCase())}
                          title={s.refund_id ? "Already refunded" : "Select to void"}/>
                      </td>
                      <td className="px-3 py-2 mono">{s.code}</td>
                      <td className="px-3 py-2">{s.customer_name || "—"}</td>
                      <td className="px-3 py-2 mono uppercase">{s.carrier || "—"}{s.byo_carrier ? " · BYO" : ""}</td>
                      <td className="px-3 py-2 mono text-[11.5px]">{s.tracking || "—"}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] bg-secondary text-foreground/70">
                          {s.tracking_status || s.status || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right mono">${Number(s.shipping_cost || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {s.label_url && (
                            <a href={s.label_url} target="_blank" rel="noreferrer"
                              className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/60 hover:text-foreground underline"
                              data-testid={`history-label-${s.id}`}>Label</a>
                          )}
                          <button onClick={() => setQrReturnRow(s)}
                            data-testid={`history-qr-${s.id}`}
                            className="text-[11px] mono uppercase tracking-[0.14em] text-indigo-700 hover:text-indigo-900 underline ml-2">
                            QR return
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bulkVoidResult && (
                <div className="px-4 py-3 border-t border-border bg-secondary/20 text-[11.5px]">
                  <div className="mono uppercase text-[10.5px] tracking-[0.14em] text-foreground/60 mb-1">Bulk void result</div>
                  {(bulkVoidResult.results || []).map((r, i) => (
                    <div key={i} className={`mono ${r.ok ? "text-emerald-700" : "text-rose-700"}`}>
                      {r.order} · {r.ok ? (r.status || "refund submitted") : `error: ${r.error || "unknown"}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase 8 · Recipient address book panel ─────────────────────── */}
      {recipientsOpen && (
        <RecipientsPanel
          recipients={recipients}
          recipientQ={recipientQ}
          setRecipientQ={setRecipientQ}
          loadRecipients={loadRecipients}
          setRecipientDraft={setRecipientDraft}
          deleteRecipient={deleteRecipient}
        />
      )}

      {/* ── Phase 8 · Carrier adjustments panel ────────────────────────── */}
      {adjustmentsOpen && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-adjustments-panel">
          <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
            <div>
              <div className="text-[13px] font-medium">Carrier adjustments</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Dim-weight surcharges + address corrections billed after label purchase.
              </div>
            </div>
            {adjustments && (
              <div className="text-[12px] mono">
                <span className="text-foreground/55">Total:</span>{" "}
                <span className={`font-medium ${adjustments.total_amount > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  ${Number(adjustments.total_amount || 0).toFixed(2)}
                </span>
              </div>
            )}
          </div>
          {!adjustments ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">Loading…</div>
          ) : adjustments.count === 0 ? (
            <div className="p-10 text-center text-[12px] text-foreground/55">
              No adjustments in the last 30 days. Your rate quotes are matching carrier billing.
            </div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="bg-secondary/30 text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Carrier</th>
                  <th className="text-left px-3 py-2">Tracking</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-right px-3 py-2">Quoted</th>
                  <th className="text-right px-3 py-2">Adjustment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {adjustments.adjustments.map((a, i) => (
                  <tr key={i} data-testid={`adj-${a.code}`}>
                    <td className="px-3 py-2 mono">{a.code}</td>
                    <td className="px-3 py-2 mono uppercase">{a.carrier}</td>
                    <td className="px-3 py-2 mono text-[11.5px]">{a.tracking}</td>
                    <td className="px-3 py-2 text-foreground/70">{a.adjustment_reason || "—"}</td>
                    <td className="px-3 py-2 text-right mono">${Number(a.shipping_cost || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right mono text-amber-700">
                      {Number(a.carrier_adjustment) > 0 ? "+" : ""}${Number(a.carrier_adjustment).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Phase 8 · Rate compare dialog ──────────────────────────────── */}
      <Dialog open={rateCompareOpen} onOpenChange={setRateCompareOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="rate-compare-dialog">
          <DialogHeader>
            <DialogTitle>Rate compare</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Order</label>
                <select value={rateCompareOid}
                  onChange={e => setRateCompareOid(e.target.value)}
                  data-testid="rate-compare-oid"
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-[12.5px]">
                  {readyOrders.map(o => (
                    <option key={o.id} value={o.id}>{o.code} · {o.customer_name || o.ship_to?.name || "customer"}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Weight (lb)</label>
                <Input type="number" step="0.1" min="0.1" value={weight}
                  onChange={e => setWeight(e.target.value)}
                  data-testid="rate-compare-weight" className="h-10"/>
              </div>
            </div>
            <Button onClick={runRateCompare} disabled={rateCompareBusy || !rateCompareOid}
              data-testid="rate-compare-run"
              className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
              {rateCompareBusy ? "Fetching rates…" : "Shop rates"}
            </Button>
            {rateCompareRates.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-secondary/30 text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                    <tr>
                      <th className="text-left px-3 py-2">Carrier</th>
                      <th className="text-left px-3 py-2">Service</th>
                      <th className="text-right px-3 py-2">Days</th>
                      <th className="text-right px-3 py-2">Rate</th>
                      <th className="text-right px-3 py-2">Savings vs highest</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rateCompareRates.map((r, i) => (
                      <tr key={r.object_id || i} className={i === 0 ? "bg-emerald-50/60" : ""} data-testid={`rate-row-${i}`}>
                        <td className="px-3 py-2 mono uppercase">{(() => {
                          const p = (r.provider || "").toLowerCase();
                          if (p === "warp" || p === "verqo-shipping") return "VERQO";
                          return r.provider || r.carrier || "—";
                        })()}</td>
                        <td className="px-3 py-2">{r.servicelevel?.name || r.service || r.service_level || "—"}</td>
                        <td className="px-3 py-2 text-right mono">{r.estimated_days ?? r.days ?? "—"}</td>
                        <td className="px-3 py-2 text-right mono font-medium">${Number(r.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right mono text-emerald-700">
                          {r.savings_vs_highest > 0 ? `-$${Number(r.savings_vs_highest).toFixed(2)}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Phase 8 · QR return confirmation dialog ────────────────────── */}
      <Dialog open={!!qrReturnRow} onOpenChange={(v) => !v && setQrReturnRow(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="qr-return-dialog">
          <DialogHeader>
            <DialogTitle>Generate QR return</DialogTitle>
          </DialogHeader>
          {qrReturnRow && (
            <div className="space-y-3 text-[12.5px]">
              <div className="rounded-xl border border-border bg-secondary/20 p-3">
                <div className="mono uppercase text-[10.5px] tracking-[0.14em] text-foreground/55">Original shipment</div>
                <div className="mt-1"><span className="mono">{qrReturnRow.code}</span> · {qrReturnRow.customer_name || "customer"}</div>
                <div className="mono text-foreground/60 text-[11.5px]">{qrReturnRow.carrier} · {qrReturnRow.tracking}</div>
              </div>
              <div className="text-foreground/70">
                Buyer scans the QR at any USPS or UPS drop-off — no printing required. Verqo Shipping charges the return label after the parcel is scanned into the network.
              </div>
              <Button onClick={requestQrReturn} disabled={qrReturnBusy}
                data-testid="qr-return-confirm"
                className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
                {qrReturnBusy ? "Generating…" : "Generate QR return label"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Phase 8 · SLA (ship-by / hold-until) dialog ────────────────── */}
      <Dialog open={slaOpen} onOpenChange={setSlaOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="sla-dialog">
          <DialogHeader>
            <DialogTitle>Apply ship-by & hold-until</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px]">
            <div className="text-foreground/70">
              Applying to <span className="mono font-medium">{selected.size}</span> selected ready {selected.size === 1 ? "order" : "orders"}.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Ship by</label>
                <Input type="date" value={slaShipBy} onChange={e => setSlaShipBy(e.target.value)}
                  data-testid="sla-ship-by" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Hold until</label>
                <Input type="date" value={slaHoldUntil} onChange={e => setSlaHoldUntil(e.target.value)}
                  data-testid="sla-hold-until" className="h-10"/>
              </div>
            </div>
            <Button onClick={submitSla} disabled={slaBusy}
              data-testid="sla-confirm"
              className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
              {slaBusy ? "Applying…" : "Apply SLA"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Phase 8 · Add recipient dialog ─────────────────────────────── */}
      <Dialog open={!!recipientDraft} onOpenChange={(v) => !v && setRecipientDraft(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="recipient-dialog">
          <DialogHeader>
            <DialogTitle>Add recipient</DialogTitle>
          </DialogHeader>
          {recipientDraft && (
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <div className="col-span-2">
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Name *</label>
                <Input value={recipientDraft.name} onChange={e => setRecipientDraft({ ...recipientDraft, name: e.target.value })} data-testid="recipient-name" className="h-10"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Company</label>
                <Input value={recipientDraft.company} onChange={e => setRecipientDraft({ ...recipientDraft, company: e.target.value })} data-testid="recipient-company" className="h-10"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Street *</label>
                <Input value={recipientDraft.street1} onChange={e => setRecipientDraft({ ...recipientDraft, street1: e.target.value })} data-testid="recipient-street1" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">City *</label>
                <Input value={recipientDraft.city} onChange={e => setRecipientDraft({ ...recipientDraft, city: e.target.value })} data-testid="recipient-city" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">State *</label>
                <Input value={recipientDraft.state} onChange={e => setRecipientDraft({ ...recipientDraft, state: e.target.value.toUpperCase() })} data-testid="recipient-state" maxLength={2} className="h-10 uppercase"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Zip *</label>
                <Input value={recipientDraft.zip} onChange={e => setRecipientDraft({ ...recipientDraft, zip: e.target.value })} data-testid="recipient-zip" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Country</label>
                <Input value={recipientDraft.country} onChange={e => setRecipientDraft({ ...recipientDraft, country: e.target.value.toUpperCase() })} data-testid="recipient-country" maxLength={2} className="h-10 uppercase"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Phone</label>
                <Input value={recipientDraft.phone} onChange={e => setRecipientDraft({ ...recipientDraft, phone: e.target.value })} data-testid="recipient-phone" className="h-10"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Email</label>
                <Input value={recipientDraft.email} onChange={e => setRecipientDraft({ ...recipientDraft, email: e.target.value })} data-testid="recipient-email" className="h-10"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Tag (optional)</label>
                <Input value={recipientDraft.tag} onChange={e => setRecipientDraft({ ...recipientDraft, tag: e.target.value })} data-testid="recipient-tag" className="h-10" placeholder="e.g. dc-west, wholesale, vip"/>
              </div>
              <div className="col-span-2">
                <Button onClick={saveRecipient}
                  data-testid="recipient-save"
                  className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90">
                  Save recipient
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Postage Wallet — manage / top-up dialog (Verqo-owned wallet) ── */}
      <PostageWalletDialog
        open={postageDialogOpen}
        onOpenChange={setPostageDialogOpen}
        initialSource={postageTopupSource}
        balance={balance}
        paymentMethods={paymentMethods}
        onChanged={loadPostageWallet}
      />

      {/* ── Queues — full management panel in a dialog (chip strip → Manage) ── */}
      <Dialog open={queuesDialogOpen} onOpenChange={setQueuesDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="queues-manage-dialog">
          <DialogHeader>
            <DialogTitle>Manage order queues</DialogTitle>
          </DialogHeader>
          <div className="text-[11.5px] text-foreground/60 -mt-2 mb-3">
            Queues are saved filters over your ready-to-ship orders. Create smart filters
            (status, carrier, value, tags…) plus manual pins/exclusions. Chips at the top of
            the Orders tab give one-click access to each queue.
          </div>
          <OrderQueuesPanel />
        </DialogContent>
      </Dialog>

      {/* ── Draft Batches — create / edit / delete dialog ── */}
      <Dialog open={draftBatchDialogOpen} onOpenChange={(v) => { setDraftBatchDialogOpen(v); if (!v) setDraftBatchEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="draft-batch-dialog">
          <DialogHeader>
            <DialogTitle>{draftBatchEditing?.id ? "Edit draft batch" : "New draft batch"}</DialogTitle>
          </DialogHeader>
          <div className="text-[11.5px] text-foreground/60 -mt-2 mb-3">
            Pre-group ready-to-ship orders. When you buy labels for orders in this draft, the
            resulting shipment batch inherits this name so you see the same grouping in
            Shipments.
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Name</label>
              <Input
                value={draftBatchEditing?.name || ""}
                onChange={e => setDraftBatchEditing(v => ({ ...(v || {}), name: e.target.value }))}
                placeholder="e.g. Morning Batch"
                data-testid="draft-batch-name-input"
                className="h-9 text-[13px]"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Color</label>
              <div className="flex items-center gap-1.5">
                {[
                  { k: "indigo",  bg: "bg-indigo-500"  },
                  { k: "emerald", bg: "bg-emerald-500" },
                  { k: "amber",   bg: "bg-amber-500"   },
                  { k: "rose",    bg: "bg-rose-500"    },
                  { k: "sky",     bg: "bg-sky-500"     },
                  { k: "slate",   bg: "bg-slate-500"   },
                ].map(c => (
                  <button key={c.k}
                    onClick={() => setDraftBatchEditing(v => ({ ...(v || {}), color: c.k }))}
                    data-testid={`draft-batch-color-${c.k}`}
                    className={`w-6 h-6 rounded-full ${c.bg} ${draftBatchEditing?.color === c.k ? "ring-2 ring-offset-2 ring-foreground" : "opacity-70 hover:opacity-100"}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Notes (optional)</label>
              <Input
                value={draftBatchEditing?.notes || ""}
                onChange={e => setDraftBatchEditing(v => ({ ...(v || {}), notes: e.target.value }))}
                placeholder="Anything the packer should know"
                data-testid="draft-batch-notes-input"
                className="h-9 text-[13px]"
              />
            </div>
            {draftBatchEditing?.order_ids?.length > 0 && !draftBatchEditing?.id && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-900">
                {draftBatchEditing.order_ids.length} order{draftBatchEditing.order_ids.length === 1 ? "" : "s"} will be added on save.
              </div>
            )}
            {draftBatchEditing?.id && (
              <div className="rounded-md border border-border px-3 py-2 text-[11px] text-foreground/60">
                <div>Open orders: <span className="mono tabular-nums">{draftBatchEditing.counts?.open ?? 0}</span></div>
                <div>Shipped: <span className="mono tabular-nums">{draftBatchEditing.counts?.shipped ?? 0}</span></div>
                {draftBatchEditing.purchased_at && <div className="text-emerald-700 mt-1">Fully purchased ✓</div>}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 mt-4">
            {draftBatchEditing?.id && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete draft batch "${draftBatchEditing.name}"? Orders are not affected.`)) return;
                  try {
                    await api.delete(`/dropship/draft-batches/${draftBatchEditing.id}`);
                    toast.success("Draft batch deleted");
                    if (activeDraftBatchId === draftBatchEditing.id) setActiveDraftBatchId(null);
                    await loadDraftBatches();
                    setDraftBatchDialogOpen(false);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Delete failed");
                  }
                }}
                data-testid="draft-batch-delete"
                className="text-[11px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                Delete
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setDraftBatchDialogOpen(false)}
                className="text-[11px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const name = (draftBatchEditing?.name || "").trim();
                  if (!name) return toast.error("Name required");
                  try {
                    if (draftBatchEditing?.id) {
                      await api.patch(`/dropship/draft-batches/${draftBatchEditing.id}`, {
                        name, color: draftBatchEditing.color, notes: draftBatchEditing.notes || null,
                      });
                      toast.success("Draft batch updated");
                    } else {
                      const r = await api.post("/dropship/draft-batches", {
                        name, color: draftBatchEditing?.color || "indigo",
                        notes: draftBatchEditing?.notes || null,
                        order_ids: draftBatchEditing?.order_ids || [],
                      });
                      toast.success(`Draft batch "${name}" created`);
                      setActiveDraftBatchId(r.data?.id || null);
                    }
                    await loadDraftBatches();
                    setDraftBatchDialogOpen(false);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Save failed");
                  }
                }}
                data-testid="draft-batch-save"
                className="text-[11px] mono uppercase tracking-[0.14em] rounded-full h-8 px-4 bg-foreground text-background hover:bg-foreground/90"
              >
                {draftBatchEditing?.id ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PostageWalletDialog — top-up (from wallet / from payment method) +
// auto-refill configuration. Isolated ledger — sellers no longer own a
// Verqo Shipping account; Verqo brokers all label buys.
// ─────────────────────────────────────────────────────────────────────
function PostageWalletDialog({ open, onOpenChange, initialSource, balance, paymentMethods, onChanged }) {
  const [source, setSource] = useState(initialSource || "wallet");
  const [amount, setAmount] = useState("25");
  const [pmid, setPmid] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(false);
  const [autoRefillThreshold, setAutoRefillThreshold] = useState("25");
  const [autoRefillAmount, setAutoRefillAmount] = useState("50");
  const [autoRefillSource, setAutoRefillSource] = useState("wallet");
  const [arBusy, setArBusy] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (open) {
      setSource(initialSource || "wallet");
      setAmount("25");
      setPmid(paymentMethods?.[0]?.id || "");
      const ar = balance?.auto_refill || {};
      setAutoRefillEnabled(!!ar.enabled);
      setAutoRefillThreshold(String((ar.threshold_cents || 2500) / 100));
      setAutoRefillAmount(String((ar.amount_cents || 5000) / 100));
      setAutoRefillSource(ar.source || "wallet");
      api.get("/postage-wallet/pricing").then(r => setPricing(r.data)).catch(() => setPricing(null));
    }
  }, [open, initialSource, paymentMethods, balance]);

  const topup = async () => {
    const cents = Math.round(Number(amount) * 100);
    if (!cents || cents <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      if (source === "wallet") {
        const r = await api.post("/postage-wallet/topup-from-wallet", { amount_cents: cents });
        toast.success(`Postage wallet + $${(r.data.amount_cents/100).toFixed(2)}`);
      } else {
        if (!pmid) return toast.error("Pick a payment method");
        const r = await api.post(`/postage-wallet/topup-from-payment-method/${pmid}`, { amount_cents: cents });
        const scAmt = Number(r.data.card_surcharge_amount || 0);
        const scPct = Number(r.data.card_surcharge_pct || 0);
        const chargedAmt = Number(r.data.card_charged_amount || r.data.amount || 0);
        if (scAmt > 0) {
          toast.success(
            `Postage +$${(r.data.amount_cents/100).toFixed(2)} · charged $${chargedAmt.toFixed(2)} to card ` +
            `(incl. $${scAmt.toFixed(2)} ${scPct.toFixed(1)}% CC surcharge)${r.data.charged_live ? "" : " · demo"}`
          );
        } else {
          toast.success(`Charged $${(r.data.amount_cents/100).toFixed(2)} · postage wallet updated${r.data.charged_live ? "" : " (demo)"}`);
        }
      }
      await onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Top-up failed"));
    } finally { setBusy(false); }
  };

  const saveAutoRefill = async () => {
    setArBusy(true);
    try {
      await api.post("/postage-wallet/auto-refill", {
        enabled:            autoRefillEnabled,
        threshold_cents:    Math.round(Number(autoRefillThreshold) * 100),
        amount_cents:       Math.round(Number(autoRefillAmount) * 100),
        source:             autoRefillSource,
        payment_method_id:  autoRefillSource === "payment_method" ? pmid : null,
      });
      toast.success("Auto-refill saved");
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setArBusy(false); }
  };

  const txns = balance?.recent_txns || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="postage-wallet-dialog">
        <DialogHeader>
          <DialogTitle>Postage wallet</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="rounded-xl border border-border p-3">
            <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Balance</div>
            <div className="text-2xl font-bold tabular-nums mt-1">${Number(balance?.balance || 0).toFixed(2)}</div>
            {Number(balance?.credit_line || 0) > 0 && (
              <div className="text-[10px] text-indigo-700 mt-1 font-medium" data-testid="postage-credit-line">
                + ${Number(balance?.credit_line || 0).toFixed(2)} credit line
              </div>
            )}
          </div>
          <div className="rounded-xl border border-border p-3">
            <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Main wallet</div>
            <div className="text-2xl font-bold tabular-nums mt-1">${Number(balance?.main_wallet_balance || 0).toFixed(2)}</div>
          </div>
          <div className="rounded-xl border border-border p-3">
            <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Auto-refill</div>
            <div className={`text-2xl font-bold mt-1 ${balance?.auto_refill?.enabled ? "text-emerald-700" : "text-foreground/40"}`}>
              {balance?.auto_refill?.enabled ? "ON" : "OFF"}
            </div>
          </div>
        </div>

        {/* Shipping pricing / tier progress */}
        {pricing && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 mb-5" data-testid="postage-pricing-panel">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-indigo-900/70">Your shipping rate</div>
                <div className="text-2xl font-bold text-indigo-950 tabular-nums mt-0.5" data-testid="postage-effective-pct">
                  {Number(pricing.effective_pct || 0).toFixed(1)}%
                </div>
                <div className="text-[11px] text-indigo-900/70 mt-1">{pricing.reason}</div>
              </div>
              <div className="text-right">
                <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-indigo-900/70">30-day spend</div>
                <div className="text-lg font-semibold text-indigo-950 tabular-nums mt-0.5" data-testid="postage-spend-30d">
                  ${Number(pricing.spend_30d_usd || 0).toFixed(2)}
                </div>
                {pricing.next_tier && (
                  <div className="text-[10.5px] text-indigo-900/70 mt-1">
                    Next: {pricing.next_tier.label} @ {Number(pricing.next_tier.markup_pct).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
            {pricing.next_tier && (
              <div className="mt-3">
                <div className="h-1.5 rounded-full bg-indigo-100 overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 transition-all"
                    style={{
                      width: `${Math.min(100, (Number(pricing.spend_30d_usd || 0) / Number(pricing.next_tier.threshold_usd || 1)) * 100).toFixed(1)}%`,
                    }}
                  />
                </div>
                <div className="text-[10.5px] text-indigo-900/60 mt-1">
                  ${(Number(pricing.next_tier.threshold_usd || 0) - Number(pricing.spend_30d_usd || 0)).toFixed(0)} to unlock {pricing.next_tier.label}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top-up section */}
        <div className="rounded-xl border border-border p-4 mb-5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 font-medium mb-3">Top up postage</div>
          <div className="flex items-center gap-2 mb-3">
            {[
              { k: "wallet", label: "From wallet",       hint: `Move from your main wallet ($${Number(balance?.main_wallet_balance || 0).toFixed(2)} avail)` },
              { k: "card",   label: "From payment method", hint: "Charge a saved card or bank account" },
            ].map(t => (
              <button key={t.k}
                onClick={() => setSource(t.k)}
                data-testid={`postage-topup-tab-${t.k}`}
                className={`flex-1 h-16 px-3 rounded-lg border text-left transition-colors ${
                  source === t.k
                    ? "border-indigo-600 bg-indigo-50 text-indigo-900"
                    : "border-border bg-white hover:bg-secondary/40"
                }`}>
                <div className="text-[12.5px] font-semibold">{t.label}</div>
                <div className="text-[10.5px] text-foreground/60 mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Amount (USD)</Label>
              <Input type="number" min="1" step="1" value={amount} onChange={e => setAmount(e.target.value)} data-testid="postage-amount-input"/>
            </div>
            {source === "card" && (
              <div className="col-span-2">
                <Label className="text-xs">Payment method</Label>
                <select
                  value={pmid} onChange={e => setPmid(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background text-sm px-2"
                  data-testid="postage-pm-select"
                >
                  {paymentMethods.length === 0
                    ? <option value="">— No saved methods —</option>
                    : paymentMethods.map(m => (
                        <option key={m.id} value={m.id}>{m.type} · ••••{m.last4 || "----"}{m.default ? " · default" : ""}</option>
                      ))}
                </select>
              </div>
            )}
            {source === "wallet" && <div className="col-span-2"/>}
          </div>

          <Button onClick={topup} disabled={busy} className="mt-3 w-full" data-testid="postage-topup-submit">
            {busy ? "Processing…" : (source === "wallet" ? "Transfer to postage" : "Charge & top up")}
          </Button>
          {source === "card" && (() => {
            const pm = paymentMethods.find(m => m.id === pmid);
            const isCard = (pm?.type || "").toLowerCase() === "card";
            if (!isCard) {
              return (
                <div className="mt-2 text-[11px] text-foreground/55" data-testid="postage-cc-surcharge-none">
                  Bank / ACH top-ups have no surcharge.
                </div>
              );
            }
            const pct = Number(balance?.card_surcharge?.pct || 4.0);
            const amt = Number(amount) || 0;
            const sc  = Math.round(amt * pct) / 100;
            const total = amt + sc;
            const planLabel = (balance?.card_surcharge?.plan_id || "starter").toLowerCase() === "starter"
              ? "Starter" : "Growth/Pro/Enterprise";
            return (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900" data-testid="postage-cc-surcharge-preview">
                <div className="flex items-center justify-between">
                  <span>Credit-card surcharge ({pct.toFixed(1)}% · {planLabel})</span>
                  <span className="tabular-nums font-semibold">+ ${sc.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-amber-200/70">
                  <span className="font-medium">Card will be charged</span>
                  <span className="tabular-nums font-bold">${total.toFixed(2)}</span>
                </div>
                <div className="text-[10.5px] text-amber-800/80 mt-1">Postage wallet receives the full ${amt.toFixed(2)}. Surcharge covers card-network fees.</div>
              </div>
            );
          })()}
        </div>

        {/* Auto-refill section */}
        <div className="rounded-xl border border-border p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[13px] font-semibold">Auto-refill</div>
              <div className="text-[11px] text-foreground/55">Top up automatically when the balance drops below a threshold.</div>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={autoRefillEnabled} onChange={e => setAutoRefillEnabled(e.target.checked)} data-testid="postage-autorefill-toggle" className="mr-2"/>
              <span className="text-[12px]">{autoRefillEnabled ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Refill when balance &lt; ($)</Label>
              <Input type="number" min="0" step="1" value={autoRefillThreshold} onChange={e => setAutoRefillThreshold(e.target.value)} disabled={!autoRefillEnabled}/>
            </div>
            <div>
              <Label className="text-xs">Refill amount ($)</Label>
              <Input type="number" min="1" step="1" value={autoRefillAmount} onChange={e => setAutoRefillAmount(e.target.value)} disabled={!autoRefillEnabled}/>
            </div>
            <div>
              <Label className="text-xs">Refill from</Label>
              <select
                value={autoRefillSource} onChange={e => setAutoRefillSource(e.target.value)}
                disabled={!autoRefillEnabled}
                className="w-full h-9 rounded-md border border-input bg-background text-sm px-2"
              >
                <option value="wallet">Main wallet</option>
                <option value="payment_method">Payment method</option>
              </select>
            </div>
          </div>
          <Button onClick={saveAutoRefill} disabled={arBusy} variant="outline" className="mt-3">
            {arBusy ? "Saving…" : "Save auto-refill"}
          </Button>
        </div>

        {/* Recent transactions */}
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-2 border-b border-border bg-muted/30">
            <div className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 font-medium">Recent activity</div>
          </div>
          {txns.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-foreground/50">No transactions yet</div>
          ) : (
            <div className="divide-y divide-border">
              {txns.slice(0, 10).map(t => {
                const cat = t.category || "other";
                const catStyle = {
                  postage_funding: { bg: "bg-indigo-50",  fg: "text-indigo-700",  label: "Postage funds" },
                  shipping:        { bg: "bg-rose-50",    fg: "text-rose-700",    label: "Shipping" },
                  shipping_refund: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Refund" },
                  other:           { bg: "bg-slate-100",  fg: "text-slate-600",   label: "Other" },
                }[cat] || { bg: "bg-slate-100", fg: "text-slate-600", label: cat };
                return (
                  <div key={t.id} className="px-4 py-2.5 flex items-center justify-between text-[12.5px] gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{(t.kind || "").replace(/_/g, " ")}</span>
                        <span className={`text-[9.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded font-semibold ${catStyle.bg} ${catStyle.fg}`}>
                          {catStyle.label}
                        </span>
                      </div>
                      <div className="text-[10.5px] mono text-foreground/50 truncate">
                        {t.ref}
                        {t.meta?.order_code ? ` · ${t.meta.order_code}` : ""}
                        {t.meta?.carrier ? ` · ${t.meta.carrier}` : ""}
                        {t.meta?.description ? ` · ${t.meta.description}` : ""}
                      </div>
                    </div>
                    <div className={`tabular-nums font-semibold shrink-0 ${t.amount_cents >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {t.amount_cents >= 0 ? "+" : "-"}${Math.abs(t.amount_cents / 100).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
